import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2, AlertCircle, CheckCircle2, TrendingUp, TrendingDown,
  Zap, Star, MessageCircle, CreditCard, Calendar, History, XCircle,
  Receipt, Building2, BanIcon, ShieldCheck, FileText, ChevronDown,
} from 'lucide-react';
import {
  planApi, receiptInfoApi, PlanCode, PlanInfo, UsageInfo, PlanChangeResponse, PaymentRecord,
  SubscriptionStatus, ReceiptType, CashReceiptPurpose, ReceiptInfo, PaymentMethod,
  EnterpriseContract, EnterpriseContractRequest, EnterprisePriceResponse, enterpriseApi,
  CardRegisterRequest, CardRegisterResponse, CardInfo, CardListResponse,
  ProrationRequest, NormalProrationResponse, EnterpriseProrationResponse,
} from '@/api/planApi';
import { dashboardApi, AiUsageStatus, BillingPeriod } from '@/api/dashboardApi';
import { teamApi } from '@/api/teamApi';
import { DowngradeReason } from '@/api/planApi';
import { workspaceApi } from '@/api/workspaceApi';
import { validateAndBuildPersonalPlanOverlay, PersonalPlanOverlayFields } from '@/lib/workspacePlanOverlay';
import { isCardPaymentEnabled, isWorkspaceApiEnabled } from '@/lib/featureFlags';
import { isBackBillPending } from '@/lib/planUtils';
import { useDowngradePolicy } from '@/hooks/useDowngradePolicy';
import { getDowngradePolicyCardText } from '@/lib/downgradePolicyText';

/** workspaceApi 응답이 4초 안에 안 오면 타임아웃으로 간주하고 abort - legacy 그대로 폴백 */
const WORKSPACE_PLAN_OVERLAY_TIMEOUT_MS = 4000;

// ── 다운그레이드 불가 사유 메시지 ─────────────────────────────────

function getDowngradeMessage(reason: DowngradeReason): string {
  switch (reason) {
    case 'FREE_PLAN':
      return 'FREE 플랜은 최하위 플랜이므로 다운그레이드가 불가능합니다.';
    case 'STARTER_PLAN':
      return 'STARTER 플랜은 최하위 플랜이므로 다운그레이드가 불가능합니다.';
    case 'MONTHLY_LIMIT_EXCEEDED':
      return '이번 달 다운그레이드 횟수가 1회 사용되어 다운그레이드가 불가능합니다. 월 1회 다운그레이드 제한이 적용됩니다.';
    case 'PENDING_UPGRADE':
      return '업그레이드가 진행 중입니다. 업그레이드 완료 후 다운그레이드가 가능합니다.';
    case 'NOT_OWNER':
      return '팀원은 플랜 변경이 불가능합니다. 팀장에게 문의하세요.';
    default:
      return '다운그레이드가 불가능합니다.';
  }
}

// ── 증빙 설정 헬퍼 ───────────────────────────────────────────

function fmtPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function fmtBizNo(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

interface ReceiptFormState {
  receiptType: ReceiptType;
  cashReceiptPurpose: CashReceiptPurpose;
  phoneNumber: string;
  cashReceiptBizNo: string;
  taxInvoiceBizNo: string;
  companyName: string;
  ceoName: string;
  invoiceEmail: string;
}

const EMPTY_RECEIPT_FORM: ReceiptFormState = {
  receiptType: 'NONE',
  cashReceiptPurpose: 'PERSONAL_DEDUCTION',
  phoneNumber: '',
  cashReceiptBizNo: '',
  taxInvoiceBizNo: '',
  companyName: '',
  ceoName: '',
  invoiceEmail: '',
};

// ── 플랜 메타 정보 ────────────────────────────────────────────

interface PlanMeta {
  code: PlanCode;
  name: string;
  price: number | null;
  monthlyLimit: number | null;
  features: string[];
  badge?: string;
  badgeColor?: string;
  color: string;
  borderColor: string;
  bgColor: string;
}

const PLANS: PlanMeta[] = [
  {
    code: 'FREE',
    name: '무료',
    price: 0,
    monthlyLimit: 60,
    features: ['월 60개 이미지', '기본 기능'],
    color: 'text-gray-700',
    borderColor: 'border-gray-200',
    bgColor: 'bg-gray-50',
  },
  {
    code: 'STARTER',
    name: '스타트',
    price: 9900,
    monthlyLimit: 1000,
    features: ['월 1,000개 이미지', '모든 기능', '서비스 지원', 'API 접근'],
    color: 'text-brand-blue',
    borderColor: 'border-brand-blue-light',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'PROFESSIONAL',
    name: '프로',
    price: 29000,
    monthlyLimit: 5000,
    features: ['월 5,000개 이미지', '모든 기능', '우선 지원', 'API 접근'],
    badge: '인기',
    badgeColor: 'bg-brand-orange text-white',
    color: 'text-brand-blue-dark',
    borderColor: 'border-brand-blue',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'ENTERPRISE',
    name: '엔터프라이즈',
    price: null,
    monthlyLimit: null,
    features: ['5천건 초과 단가 할인', '대량 사용 누진 요금제', '맞춤 기능', '전담 매니저', 'SLA 보장'],
    color: 'text-brand-purple',
    borderColor: 'border-brand-purple-light',
    bgColor: 'bg-purple-50',
  },
];

const PLAN_ORDER: PlanCode[] = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

function getPlanMeta(code: PlanCode): PlanMeta {
  return PLANS.find(p => p.code === code) ?? PLANS[0];
}

function getPlanLevel(code: PlanCode): number {
  return PLAN_ORDER.indexOf(code);
}

function getChangeType(from: PlanCode, to: PlanCode): 'UPGRADE' | 'DOWNGRADE' {
  return getPlanLevel(to) > getPlanLevel(from) ? 'UPGRADE' : 'DOWNGRADE';
}

/**
 * 비활성화된 버튼 위에 마우스를 올렸을 때 사유를 보여주기 위한 래퍼.
 * shadcn Button은 disabled 시 `disabled:pointer-events-none`을 적용해 버튼 자체가
 * 마우스 이벤트를 받지 못하므로, title 속성을 버튼에 직접 줘도 네이티브 툴팁이 뜨지 않는다.
 * pointer-events가 살아있는 바깥 span에 title을 옮겨서 hover 시 사유가 보이게 한다.
 */
function DisabledHint({ title, children }: { title?: string; children: React.ReactNode }) {
  if (!title) return <>{children}</>;
  return <span className="block w-full" title={title}>{children}</span>;
}

function localizePlanCodeMessage(message: string): string {
  return message
    .replace(/\bPROFESSIONAL\b/g, '프로')
    .replace(/\bSTARTER\b/g, '스타트')
    .replace(/\bENTERPRISE\b/g, '엔터프라이즈')
    .replace(/\bFREE\b/g, '무료');
}

/** 기존 PlanInfo만 있을 때 subscriptionStatus 추론 (B/E 신규 엔드포인트 준비 전 fallback) */
function deriveSubscriptionStatus(plan: PlanInfo, hasPendingPayment: boolean): SubscriptionStatus {
  if (plan.hasPendingUpgrade || hasPendingPayment) return 'PENDING_UPGRADE';
  if (plan.pendingPlan !== null) return 'PENDING_DOWNGRADE';
  if (plan.currentPlan !== 'FREE') return 'ACTIVE_PAID';
  return 'FREE';
}

const SUBSCRIPTION_STATUS_META: Record<SubscriptionStatus, { label: string; className: string }> = {
  FREE:               { label: '무료 이용 중',       className: 'bg-gray-100 text-gray-600 border-gray-300' },
  PENDING_UPGRADE:    { label: '입금 대기',          className: 'bg-blue-100 text-blue-700 border-blue-300' },
  ACTIVE_PAID:        { label: '유료 이용 중',       className: 'bg-green-100 text-green-700 border-green-300' },
  PENDING_DOWNGRADE:  { label: '다운그레이드 예약',   className: 'bg-amber-100 text-amber-700 border-amber-300' },
};

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function PlanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { nonEnterpriseDowngradeAppliesFirstOfMonth } = useDowngradePolicy();

  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [pendingPayment, setPendingPayment] = useState<PaymentRecord | null>(null);
  const [latestSuccessfulPayment, setLatestSuccessfulPayment] = useState<PaymentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [cancelPaymentConfirm, setCancelPaymentConfirm] = useState<{ open: boolean; paymentId: number | null }>({ open: false, paymentId: null });
  const [cancellingPayment, setCancellingPayment] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<{ startDate: string; endDate: string; nextBillingDate: string } | null>(null);
  const [enterprisePricingTiers, setEnterprisePricingTiers] = useState<Array<{ tier: string; unitPrice: number }>>([]);

  // Modal 상태
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; targetPlan: PlanCode | null; error: string | null; proration: NormalProrationResponse | null }>({
    open: false,
    targetPlan: null,
    error: null,
    proration: null,
  });
  const [blockModal, setBlockModal] = useState<{ open: boolean; message: string; altPlan?: PlanCode }>({
    open: false,
    message: '',
  });
  const [resultModal, setResultModal] = useState<{ open: boolean; result: PlanChangeResponse | null }>({
    open: false,
    result: null,
  });
  const [downgradeResult, setDowngradeResult] = useState<{ open: boolean; message: string; effectiveDate?: string | null; nextBillingAmount?: number | null }>({
    open: false,
    message: '',
  });
  const [changing, setChanging] = useState(false);

  // 증빙 선택 Modal 상태
  const [receiptModal, setReceiptModal] = useState<{ open: boolean; targetPlan: PlanCode | null }>({
    open: false, targetPlan: null,
  });

  // 결제 방법 선택 Modal 상태
  const [paymentMethodModal, setPaymentMethodModal] = useState<{ open: boolean; targetPlan: PlanCode | null; proration: NormalProrationResponse | null }>({
    open: false, targetPlan: null, proration: null,
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('BANK_TRANSFER');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentProrationExpanded, setPaymentProrationExpanded] = useState(false);

  // 카드 등록 Modal 상태
  const [cardRegisterModal, setCardRegisterModal] = useState(false);
  const [cardRegisterTargetPlan, setCardRegisterTargetPlan] = useState<PlanCode | null>(null);  // 카드 등록 후 결제할 플랜
  const [cardForm, setCardForm] = useState<{
    cardNumber: string;
    cardExpiration: string;  // MM/YY 형식
    customerIdentityNumber: string;  // 생년월일 6자리 (YYMMDD) 또는 사업자등록번호 10자리
    cardPassword: string;
    cardOwnerName: string;
  }>({
    cardNumber: '',
    cardExpiration: '',
    customerIdentityNumber: '',
    cardPassword: '',
    cardOwnerName: '',
  });
  const [cardRegistering, setCardRegistering] = useState(false);
  const [cardRegisterError, setCardRegisterError] = useState<string | null>(null);
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardDeleteConfirm, setCardDeleteConfirm] = useState<{ open: boolean; cardId: number | null }>({
    open: false,
    cardId: null,
  });
  const [showIdentityInput, setShowIdentityInput] = useState(false);  // 자동 완성 방지용 state
  const [receiptForm, setReceiptForm] = useState<ReceiptFormState>(EMPTY_RECEIPT_FORM);
  const [receiptHasExisting, setReceiptHasExisting] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptValidating, setReceiptValidating] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [receiptLookupError, setReceiptLookupError] = useState<string | null>(null);
  const [receiptBizValidated, setReceiptBizValidated] = useState(false);
  const [receiptBizStatusLoading, setReceiptBizStatusLoading] = useState(false);
  const [receiptBizStatusWarning, setReceiptBizStatusWarning] = useState<string | null>(null);

  // 엔터프라이즈 모달 상태
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  const [enterpriseUsage, setEnterpriseUsage] = useState<number | string>('');
  const [enterprisePrice, setEnterprisePrice] = useState<number>(0);
  const [enterprisePriceInfo, setEnterprisePriceInfo] = useState<EnterprisePriceResponse | null>(null);
  const [enterpriseContract, setEnterpriseContract] = useState<EnterpriseContract | null>(null);
  const [enterpriseLoading, setEnterpriseLoading] = useState(false);
  const [enterpriseSaving, setEnterpriseSaving] = useState(false);
  const [enterpriseError, setEnterpriseError] = useState<string | null>(null);
  const [enterprisePaymentMethod, setEnterprisePaymentMethod] = useState<PaymentMethod>('BANK_TRANSFER');
  const [enterpriseAutoRenewal, setEnterpriseAutoRenewal] = useState(false);
  const [prorationExpanded, setProrationExpanded] = useState(false);
  const [prorationData, setProrationData] = useState<EnterpriseProrationResponse | null>(null);

  // 요금제 정책 안내 카드 - 탭 상태
  const [policyTab, setPolicyTab] = useState('personal');

  // 엔터프라이즈 모달 초기화
  useEffect(() => {
    if (enterpriseModalOpen) {
      setEnterpriseUsage(6000);
      setProrationExpanded(false);
      // 기존 계약이 있는 경우 autoRenewal 값 유지, 없으면 false
      setEnterpriseAutoRenewal(enterpriseContract?.autoRenewal ?? false);
      // 프롤레이션 계산 자동 호출
      handleEnterpriseUsageChange('6000');
    }
  }, [enterpriseModalOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    let planRes: Awaited<ReturnType<typeof planApi.getMyPlan>> | null = null;
    try {
      const [planResResult, usageRes, billingRes, billingPeriodRes] = await Promise.all([
        planApi.getMyPlan(),
        planApi.getMyUsage(),
        planApi.getBillingHistory({ page: 0, size: 20 }),
        dashboardApi.getBillingPeriod(),
      ]);
      planRes = planResResult;
      console.log('planRes.data:', planRes.data);
      console.log('usageRes.data:', usageRes.data);
      setPlanInfo(planRes.data);
      setUsageInfo(usageRes.data);
      setBillingPeriod(billingPeriodRes);

      // 엔터프라이즈 가격표 동적 로드
      console.log('Loading enterprise pricing tiers...');
      const tierUsages = [5001, 6000, 11001, 21001];
      const tierPromises = tierUsages.map(usage =>
        enterpriseApi.calculatePrice(usage).then(res => {
          console.log(`Successfully loaded pricing tier for usage ${usage}:`, res.data);
          return {
            tier: res.data.tier,
            unitPrice: res.data.unitPrice
          };
        }).catch((err) => {
          console.error(`Failed to load pricing tier for usage ${usage}:`, err);
          return null;
        })
      );
      const tierResults = await Promise.all(tierPromises);
      console.log('All pricing tier results:', tierResults);
      setEnterprisePricingTiers(tierResults.filter(Boolean) as Array<{ tier: string; unitPrice: number }>);
      const billingRecords = billingRes.data.content ?? [];

      // PENDING 상태의 결제 찾기 (입금 대기 중)
      const pendingList = billingRecords
        .filter((item) => item.status === 'PENDING')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setPendingPayment(pendingList[0] ?? null);
      
      // DONE/CONFIRMED 상태의 최신 결제 찾기 (현재 플랜 결정용)
      const successfulList = billingRecords
        .filter((item) => item.status === 'DONE' || item.status === 'CONFIRMED')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setLatestSuccessfulPayment(successfulList[0] ?? null);
    } catch {
      setError('요금제 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }

    // 엔터프라이즈 계약은 별도로 조회 (실패해도 다른 데이터는 표시)
    // 팀 소속인 경우 팀 계약을, 아니면 개인 계약을 조회해야 함 - 이전엔 팀
    // 컨텍스트에서도 항상 개인 계약(getMyContract)만 조회해서, 팀 계약
    // 수정/생성 판단(현재 사용량과 비교해 업그레이드/다운그레이드 결정)이
    // 팀장의 개인 계약 데이터를 기준으로 잘못 이뤄지던 버그가 있었음.
    try {
      const isTeam = planRes?.data?.isTeamContext ?? false;
      const teamIdForContract = planRes?.data?.teamId;
      const contractRes = isTeam && teamIdForContract
        ? await enterpriseApi.getTeamContract(teamIdForContract)
        : await enterpriseApi.getMyContract();
      setEnterpriseContract(contractRes.data ?? null);
    } catch {
      // 엔터프라이즈 계약 조회 실패 시 무시 (엔터프라이즈가 아닌 사용자일 수 있음)
      setEnterpriseContract(null);
    }

    // 카드 목록 조회
    loadCards();
  }, []);

  useEffect(() => { load(); }, [load]);

  // AI 사용량 상태 (팀 컨텍스트 포함)
  const { data: aiUsageStatus } = useQuery({
    queryKey: ['aiUsageStatus'],
    queryFn: () => dashboardApi.getAiUsageStatus(),
  });

  // workspaceApi.getMyPlan()으로 monthlyLimit/billingCycleDay/canDowngrade/
  // downgradeReason/changePlanReason만 덮어쓰는 overlay (개인 컨텍스트 전용,
  // src/lib/workspacePlanOverlay.ts 참고). planInfo(legacy)가 계속 유일한 진실
  // 소스이며, handleChangePlan 등 실제 변경 요청 핸들러는 이 overlay를 참조하지
  // 않고 planInfo를 직접 읽는다 - 실패(에러/타임아웃/검증 실패) 시 legacy 그대로
  // 렌더링되므로 화면이 멈추는 경로가 없다.
  const [personalPlanOverlay, setPersonalPlanOverlay] = useState<PersonalPlanOverlayFields | null>(null);

  useEffect(() => {
    if (!isWorkspaceApiEnabled || !planInfo || planInfo.isTeamContext) {
      setPersonalPlanOverlay(null);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WORKSPACE_PLAN_OVERLAY_TIMEOUT_MS);

    workspaceApi
      .getMyPlan({ signal: controller.signal })
      .then((res) => {
        const overlay = validateAndBuildPersonalPlanOverlay(res.data);
        setPersonalPlanOverlay(overlay);
        if (overlay === null) {
          console.warn('[workspace-api] invalid getMyPlan response, falling back to legacy display');
        }
      })
      .catch((err) => {
        console.warn('[workspace-api] getMyPlan failed/timed out, falling back to legacy display', err);
        setPersonalPlanOverlay(null);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [planInfo]);

  // ── 카드 관련 핸들러 ───────────────────────────────────────────

  const loadCards = async () => {
    setLoadingCards(true);
    try {
      const res = await planApi.getCards();
      console.log('카드 목록 응답:', res.data);
      // 백엔드 응답 구조: res.data가 직접 배열
      setCards(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('카드 목록 조회 실패:', err);
      // 카드 목록 조회 실패 시 무시 (카드 등록 안된 사용자일 수 있음)
      setCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const handleCardRegister = async () => {
    // 카드번호 유효성 검사
    const cardNumber = cardForm.cardNumber.replace(/\D/g, '');
    if (cardNumber.length < 13 || cardNumber.length > 19) {
      setCardRegisterError('유효한 카드번호를 입력하세요.');
      return;
    }
    // 유효기간 MM/YY 검사
    const expiryClean = cardForm.cardExpiration.replace(/\D/g, '');
    if (expiryClean.length !== 4) {
      setCardRegisterError('유효기간을 MM/YY 형식으로 입력하세요 (예: 11/27).');
      return;
    }
    const month = expiryClean.slice(0, 2);
    const year = expiryClean.slice(2, 4);
    const monthNum = parseInt(month, 10);
    if (monthNum < 1 || monthNum > 12) {
      setCardRegisterError('유효한 월(01-12)을 입력하세요.');
      return;
    }
    if (!cardForm.customerIdentityNumber) {
      setCardRegisterError('생년월일(YYMMDD) 또는 사업자등록번호(10자리)를 입력하세요.');
      return;
    }
    const identityNumber = cardForm.customerIdentityNumber.replace(/\D/g, '');
    if (identityNumber.length !== 6 && identityNumber.length !== 10) {
      setCardRegisterError('생년월일은 6자리(YYMMDD), 사업자등록번호는 10자리여야 합니다.');
      return;
    }
    if (!cardForm.cardPassword || cardForm.cardPassword.length < 2) {
      setCardRegisterError('카드 비밀번호 앞 2자리를 입력하세요.');
      return;
    }
    if (!cardForm.cardOwnerName.trim()) {
      setCardRegisterError('카드 소유자명을 입력하세요.');
      return;
    }

    setCardRegistering(true);
    setCardRegisterError(null);
    try {
      // 연도 2자리를 4자리로 변환 (YY → 20YY)
      const fullYear = `20${year}`;
      const res = await planApi.registerCard({
        customerKey: 'user', // TODO: 백엔드에서 userId 제공 필요 (현재 임시값 사용)
        cardNumber,
        cardExpirationYear: fullYear,
        cardExpirationMonth: month,
        customerIdentityNumber: identityNumber,
        cardPassword: cardForm.cardPassword,
        cardOwnerName: cardForm.cardOwnerName.trim(),
        isDefault: cards.length === 0, // 첫 카드 등록 시 기본 카드로 설정
      });
      console.log('카드 등록 응답:', res.data);
      if (res.data.success) {
        setCardRegisterModal(false);
        setCardForm({
          cardNumber: '',
          cardExpiration: '',
          customerIdentityNumber: '',
          cardPassword: '',
          cardOwnerName: '',
        });
        await loadCards();
        // 카드 등록 후 결제 방법 선택 모달 다시 열기
        if (cardRegisterTargetPlan === 'ENTERPRISE') {
          setEnterpriseModalOpen(true);
          setCardRegisterTargetPlan(null);
        } else if (cardRegisterTargetPlan) {
          setPaymentMethodModal({ open: true, targetPlan: cardRegisterTargetPlan, proration: null });
          setPaymentProrationExpanded(false);
          setCardRegisterTargetPlan(null);
        }
      } else {
        setCardRegisterError('카드 등록에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('카드 등록 에러:', err);
      setCardRegisterError(err?.response?.data?.error?.message || '카드 등록 중 오류가 발생했습니다.');
    } finally {
      setCardRegistering(false);
    }
  };

  const handleCardDelete = (cardId: number) => {
    setCardDeleteConfirm({ open: true, cardId });
  };

  const handleConfirmCardDelete = async () => {
    if (!cardDeleteConfirm.cardId) return;
    try {
      const res = await planApi.deleteCard(cardDeleteConfirm.cardId);
      console.log('카드 삭제 응답:', res.data);
      setCardDeleteConfirm({ open: false, cardId: null });
      await loadCards();
    } catch (err: any) {
      console.error('카드 삭제 에러:', err);
      window.alert(err?.response?.data?.message || '카드 삭제 중 오류가 발생했습니다.');
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 16);
    return v.replace(/(\d{4})(?=\d)/g, '$1-');
  };

  const formatCardExpiration = (value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 3) {
      return `${v.slice(0, 2)}/${v.slice(2)}`;
    }
    return v;
  };

  const formatDateWithDay = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = days[date.getDay()];
    return `${year}. ${month}. ${day}.(${dayOfWeek})`;
  };

  // ── 증빙 설정 핸들러 ────────────────────────────────────────

  const openReceiptModal = async (targetPlan: PlanCode) => {
    setReceiptError(null);
    setReceiptLookupError(null);
    setReceiptBizValidated(false);
    setReceiptBizStatusWarning(null);
    setReceiptModal({ open: true, targetPlan });
    setReceiptLoading(true);
    try {
      const res = await receiptInfoApi.get();
      const info = res.data;
      if (info && info.receiptType) {
        setReceiptHasExisting(true);
        const isCashPersonal = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'PERSONAL_DEDUCTION';
        const isCashBusiness = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'BUSINESS_EXPENSE';
        setReceiptForm({
          receiptType: info.receiptType ?? 'NONE',
          cashReceiptPurpose: info.cashReceiptPurpose ?? 'PERSONAL_DEDUCTION',
          phoneNumber: isCashPersonal ? (info.cashReceiptNumberMasked ?? '') : '',
          cashReceiptBizNo: isCashBusiness ? (info.cashReceiptNumberMasked ?? '') : '',
          taxInvoiceBizNo: info.bizRegNoMasked ?? '',
          companyName: info.bizName ?? '',
          ceoName: info.bizCeoName ?? '',
          invoiceEmail: info.taxInvoiceEmail ?? '',
        });
        if (info.bizName || info.bizCeoName) setReceiptBizValidated(true);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        setReceiptError('증빙 설정을 불러오지 못했습니다.');
      }
      setReceiptHasExisting(false);
      setReceiptForm(EMPTY_RECEIPT_FORM);
    } finally {
      setReceiptLoading(false);
    }
  };

  const isMaskedValue = (v: string) => v.includes('*');

  const validateReceiptForm = (): string | null => {
    const { receiptType, cashReceiptPurpose, phoneNumber, cashReceiptBizNo, taxInvoiceBizNo, invoiceEmail } = receiptForm;
    console.log('[validateReceiptForm] receiptType:', receiptType, 'taxInvoiceBizNo:', taxInvoiceBizNo, 'isMasked:', isMaskedValue(taxInvoiceBizNo));
    if (receiptType === 'CASH_RECEIPT') {
      if (cashReceiptPurpose === 'PERSONAL_DEDUCTION') {
        if (!isMaskedValue(phoneNumber)) {
          const d = phoneNumber.replace(/\D/g, '');
          if (!d) return '휴대폰번호를 입력하세요.';
          if (d.length < 10 || d.length > 11) return '유효한 휴대폰번호를 입력하세요.';
          if (!d.startsWith('01')) return '휴대폰번호는 01로 시작해야 합니다.';
        }
      } else {
        if (!isMaskedValue(cashReceiptBizNo)) {
          const d = cashReceiptBizNo.replace(/\D/g, '');
          if (!d) return '사업자등록번호를 입력하세요.';
          if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
        }
      }
    } else if (receiptType === 'TAX_INVOICE') {
      // 마스킹된 값이 있는 경우 모든 검증 건너뜀 (기존 설정 유지)
      console.log('[validateReceiptForm] TAX_INVOICE 진입, 마스킹 체크:', isMaskedValue(taxInvoiceBizNo));
      if (isMaskedValue(taxInvoiceBizNo)) {
        console.log('[validateReceiptForm] 마스킹된 값 감지, 검증 건너뜀');
        return null;
      }
      const d = taxInvoiceBizNo.replace(/\D/g, '');
      if (!d) return '사업자등록번호를 입력하세요.';
      if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
      if (!receiptForm.companyName.trim()) return '상호명을 입력하세요.';
      if (!receiptForm.ceoName.trim()) return '대표자명을 입력하세요.';
      if (!receiptBizValidated) return '사업자 진위확인을 완료해 주세요.';
      if (!invoiceEmail.trim()) return '세금계산서 수신 이메일을 입력하세요.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceEmail.trim())) return '유효한 이메일 주소를 입력하세요.';
    }
    return null;
  };

  const handleReceiptBizStatusCheck = async (rawBizNo: string) => {
    setReceiptBizStatusLoading(true);
    setReceiptBizStatusWarning(null);
    try {
      const res = await receiptInfoApi.checkBusinessStatus(rawBizNo);
      const data = res.data;
      if (data.found) {
        if (data.bizStatusCode === '03') {
          setReceiptBizStatusWarning('본 사업자는 폐업 상태이므로 세금계산서 발행이 제한됩니다. 관련 문의는 담당자(alt.generation@outlook.com)로 연락 부탁드립니다.');
        } else if (data.bizStatusCode === '02') {
          setReceiptBizStatusWarning(`${data.bizStatus ?? '휴업'} 사업자입니다. 세금계산서 발행이 불가할 수 있습니다.`);
        }
      }
    } catch {
      // 상태조회 실패는 무시 (진위확인에서 최종 검증)
    } finally {
      setReceiptBizStatusLoading(false);
    }
  };

  const handleReceiptBizValidate = async () => {
    const rawBizNo = receiptForm.taxInvoiceBizNo.replace(/\D/g, '');
    if (rawBizNo.length !== 10) {
      setReceiptLookupError('사업자등록번호 10자리를 입력하세요.');
      return;
    }
    if (!receiptForm.companyName.trim()) {
      setReceiptLookupError('상호명을 입력하세요.');
      return;
    }
    if (!receiptForm.ceoName.trim()) {
      setReceiptLookupError('대표자명을 입력하세요.');
      return;
    }
    setReceiptValidating(true);
    setReceiptLookupError(null);
    try {
      const res = await receiptInfoApi.validateBusiness(receiptForm.taxInvoiceBizNo, receiptForm.companyName.trim(), receiptForm.ceoName.trim());
      const data = res.data;
      if (!data.valid) {
        setReceiptLookupError(data.message ?? '사업자 정보가 일치하지 않습니다. 사업자번호·상호명·대표자명을 다시 확인해 주세요.');
        setReceiptBizValidated(false);
        return;
      }
      setReceiptBizValidated(true);
    } catch (err: any) {
      setReceiptLookupError(err?.response?.data?.message ?? '진위확인 중 오류가 발생했습니다.');
      setReceiptBizValidated(false);
    } finally {
      setReceiptValidating(false);
    }
  };

  const handleReceiptProceed = async () => {
    const validationError = validateReceiptForm();
    if (validationError) {
      setReceiptError(validationError);
      return;
    }
    setReceiptSaving(true);
    setReceiptError(null);
    try {
      const isCash = receiptForm.receiptType === 'CASH_RECEIPT';
      const isTax = receiptForm.receiptType === 'TAX_INVOICE';
      // 마스킹된 값(*포함)은 null로 전송 — B/E가 기존값 유지
      const payload: ReceiptInfo = {
        receiptType: receiptForm.receiptType,
        cashReceiptPurpose: isCash ? receiptForm.cashReceiptPurpose : null,
      };

      // 마스킹된 값이 아닌 경우에만 필드 추가 (기존 값 유지)
      if (isCash && receiptForm.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && !isMaskedValue(receiptForm.phoneNumber)) {
        payload.phoneNumber = receiptForm.phoneNumber.replace(/\D/g, '');
      }
      if (isCash && receiptForm.cashReceiptPurpose === 'BUSINESS_EXPENSE' && !isMaskedValue(receiptForm.cashReceiptBizNo)) {
        payload.cashReceiptBizNo = receiptForm.cashReceiptBizNo.replace(/\D/g, '');
      }
      if (isTax && !isMaskedValue(receiptForm.taxInvoiceBizNo)) {
        payload.taxInvoiceBizNo = receiptForm.taxInvoiceBizNo.replace(/\D/g, '');
      }
      if (isTax) {
        payload.companyName = receiptForm.companyName;
        payload.ceoName = receiptForm.ceoName;
        payload.invoiceEmail = receiptForm.invoiceEmail.trim();
      }
      await receiptInfoApi.save(payload);
      const targetPlan = receiptModal.targetPlan;
      setReceiptModal({ open: false, targetPlan: null });
      if (targetPlan) {
        // 증빙 설정 후 요금 변경 확인 모달 열기
        setConfirmModal({ open: true, targetPlan, error: null, proration: null });
      }
    } catch (err: any) {
      setReceiptError(err?.response?.data?.message ?? '증빙 설정 저장 중 오류가 발생했습니다.');
    } finally {
      setReceiptSaving(false);
    }
  };

  // 엔터프라이즈 모달 핸들러
  const handleEnterpriseUsageChange = async (value: string) => {
    // 빈 값이면 입력 허용 (삭제 가능)
    if (value === '' || value === '-') {
      setEnterpriseUsage('');
      setEnterprisePrice(0);
      setProrationData(null);
      return;
    }
    const usage = parseInt(value);
    if (isNaN(usage)) return;
    setEnterpriseUsage(usage);
    if (usage >= 5001 && usage <= 1000000) {
      setEnterpriseError(null);
      setEnterpriseLoading(true);

      try {
        // 백엔드 프롤레이션 API 호출 (팀 컨텍스트 확인)
        const teamContext = planInfo?.isTeamContext ?? (planInfo as any)?.teamContext ?? false;
        const teamId = planInfo?.teamId ?? aiUsageStatus?.teamId;
        const currentPlan = planInfo?.currentPlan;

        // 백엔드 API 준비되면 프롤레이션 API 사용
        // 현재는 기존 calculatePrice 사용
        const priceRes = await enterpriseApi.calculatePrice(usage);
        console.log('Enterprise price API response:', priceRes.data);
        setEnterprisePrice(priceRes.data.price);
        setEnterprisePriceInfo(priceRes.data);

        // 프롤레이션 계산
        if (teamContext && teamId) {
          const prorationRes = await enterpriseApi.calculateTeamProration(teamId, { monthlyLimit: usage, currentPlan: currentPlan || undefined });
          setProrationData(prorationRes.data);
        } else {
          const prorationRes = await enterpriseApi.calculateProration({ monthlyLimit: usage, currentPlan: currentPlan || undefined });
          setProrationData(prorationRes.data);
        }
      } catch (err) {
        console.error('Enterprise price API error:', err);
        setEnterpriseError('가격 계산 중 오류가 발생했습니다.');
        setEnterprisePrice(0);
        setProrationData(null);
      } finally {
        setEnterpriseLoading(false);
      }
    } else {
      setEnterpriseError('5,001건 ~ 1,000,000건 사이로 입력해 주세요.');
      setEnterprisePrice(0);
      setProrationData(null);
    }
  };

  const handleEnterpriseSave = async () => {
    console.log('handleEnterpriseSave 호출됨');
    if (!planInfo) return;

    const usageNum = typeof enterpriseUsage === 'number' ? enterpriseUsage : parseInt(String(enterpriseUsage));
    if (isNaN(usageNum) || usageNum < 5001 || usageNum > 1000000) {
      setEnterpriseError('5,001건 ~ 1,000,000건 사이로 입력해 주세요.');
      return;
    }

    // 현재 계약과 동일한지 체크
    if (enterpriseContract && enterpriseContract.monthlyLimit === usageNum) {
      setEnterpriseError('현재와 동일한 사용량입니다. 변경할 내용이 없으면 저장되지 않습니다.');
      return;
    }

    // 카드 결제 선택 시 카드 등록 확인
    if (enterprisePaymentMethod === 'CARD' && cards.length === 0) {
      setCardRegisterTargetPlan('ENTERPRISE');  // 카드 등록 후 엔터프라이즈 결제
      setEnterpriseModalOpen(false);
      setCardRegisterModal(true);
      return;
    }

    // 업그레이드/다운그레이드 판단
    const isUpgrade = !enterpriseContract || usageNum > enterpriseContract.monthlyLimit;
    const isDowngrade = enterpriseContract && usageNum < enterpriseContract.monthlyLimit;

    setEnterpriseSaving(true);
    setEnterpriseError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const endDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
        .toISOString().split('T')[0];

      // 팀 소속 여부 확인
      const teamContext = planInfo?.isTeamContext ?? (planInfo as any)?.teamContext ?? false;
      const teamId = planInfo?.teamId ?? (aiUsageStatus?.teamId);
      const teamName = planInfo?.teamName;

      const contractData: EnterpriseContractRequest = {
        contractName: teamContext ? `${teamName || '팀'} 엔터프라이즈 계약` : `${user?.name || '사용자'} 엔터프라이즈 계약`,
        monthlyLimit: usageNum,
        monthlyPrice: enterprisePrice,
        startDate,
        endDate,
        paymentMethod: enterprisePaymentMethod,
        usage: usageNum,
        autoRenewal: enterpriseAutoRenewal,
      };

      // 이미 활성화된 계약이 있는 경우 계약 수정 API 호출
      if (enterpriseContract) {
        let response;
        if (teamContext && teamId) {
          response = await enterpriseApi.updateTeamContract(teamId, enterpriseContract.id, contractData);
        } else {
          response = await enterpriseApi.updateMyContract(enterpriseContract.id, contractData);
        }
        const responseData = response.data?.data;
        console.log('엔터프라이즈 계약 수정 응답:', responseData);
        // 다운그레이드 응답 처리
        if (responseData?.isDowngrade) {
          setDowngradeResult({
            open: true,
            message: responseData.downgradeMessage || '다운그레이드가 예약되었습니다.',
            effectiveDate: responseData.downgradeEffectiveDate,
            nextBillingAmount: responseData.nextBillingAmount,
          });
        }
        // alert창 제거
        // const effectiveDate = response.data?.data?.effectiveDate;
        // const message = effectiveDate
        //   ? `엔터프라이즈 계약이 수정되었습니다. ${effectiveDate}부터 적용됩니다.`
        //   : response.data?.message || '엔터프라이즈 계약이 수정되었습니다.';
        // window.alert(message);
        setEnterpriseModalOpen(false);
        await load();
      } else {
        // 새로운 계약 생성: 엔터프라이즈 계약 생성 API 호출 (백엔드에서 결제 처리)
        if (teamContext && teamId) {
          await enterpriseApi.createTeamContract(teamId, contractData);
        } else {
          await enterpriseApi.createMyContract(contractData);
        }
        setEnterpriseModalOpen(false);
        await load();
        // 계좌이체인 경우 입금 페이지로 이동
        if (enterprisePaymentMethod === 'BANK_TRANSFER') {
          navigate('/dashboard/my/billing');
        }
      }
    } catch (err: any) {
      setEnterpriseError(err?.response?.data?.message ?? '엔터프라이즈 계약 저장 중 오류가 발생했습니다.');
    } finally {
      setEnterpriseSaving(false);
    }
  };

  const handleChangePlan = async (target: PlanCode) => {
    if (!planInfo || !usageInfo) return;

    const current = isTeamContext ? planInfo.teamPlan : planInfo.currentPlan;
    if (!current || current === target) return;
    const changeType = getChangeType(current, target);

    // 엔터프라이즈는 모달 열기
    if (target === 'ENTERPRISE') {
      setEnterpriseModalOpen(true);
      setEnterpriseUsage(6000);
      setEnterpriseError(null);
      // 가격 계산
      enterpriseApi.calculatePrice(6000).then(res => {
        setEnterprisePrice(res.data.price);
        setEnterprisePriceInfo(res.data);
      }).catch(() => {
        setEnterpriseError('가격 계산 중 오류가 발생했습니다.');
        setEnterprisePrice(0);
      });
      return;
    }

    // 결제 대기 상태 — 무조건 플랜 변경 차단
    if (pendingPayment) {
      setBlockModal({
        open: true,
        message: `${getPlanMeta(pendingPayment.plan).name} 플랜 요청이 입금 대기 중입니다. 입금 확인 후 추가 변경을 요청해 주세요.`,
      });
      return;
    }

    // 엔터프라이즈 계약 입금 대기 상태 체크 (취소 상태는 제외)
    if (planEnterpriseContract?.status === 'PENDING') {
      setBlockModal({
        open: true,
        message: '엔터프라이즈 계약 입금 대기 중입니다. 입금 확인 후 추가 변경을 요청해 주세요.',
      });
      return;
    }

    // 엔터프라이즈 계약이 활성 상태인 경우 일반 플랜으로 변경 차단
    if (planEnterpriseContract?.status === 'ACTIVE') {
      setBlockModal({
        open: true,
        message: '엔터프라이즈 계약이 활성 상태입니다. 계약 만료 후 일반 플랜으로 변경할 수 있습니다.',
      });
      return;
    }

    // 다운그레이드 가능 여부 확인
    if (changeType === 'DOWNGRADE') {
      const canDowngradeCurrent = isTeamContext
        ? (planInfo?.teamCanDowngrade ?? false)
        : (planInfo?.canDowngrade ?? false);
      if (!canDowngradeCurrent) {
        setBlockModal({
          open: true,
          message: '다운그레이드가 불가능합니다.',
        });
        return;
      }
    }

    // 다운그레이드 시 사용량 체크
    if (changeType === 'DOWNGRADE') {
      const targetMeta = getPlanMeta(target);
      const limit = targetMeta.monthlyLimit ?? 0;
      const currentUsedCount = isTeamContext
        ? (usageInfo?.teamUsedCount ?? 0)
        : (usageInfo?.usedCount ?? 0);
      if (currentUsedCount > limit) {
        const altPlan = PLANS.find(
          p => p.monthlyLimit !== null && p.monthlyLimit >= currentUsedCount &&
               getPlanLevel(p.code) < getPlanLevel(current!) &&
               getPlanLevel(p.code) > getPlanLevel(target)
        );
        setBlockModal({
          open: true,
          message: `이번 달 AI 사용량이 ${currentUsedCount.toLocaleString()}건입니다. ${targetMeta.name} 플랜(${limit.toLocaleString()}건)으로 변경하시면 이번 달 서비스 이용이 제한될 수 있어 변경이 불가합니다.`,
          altPlan: altPlan?.code,
        });
        return;
      }
    }

    // 업그레이드/다운그레이드 모두 결제 방법 선택 모달 표시
    // 카드 결제 선택 시 증빙 설정 모달 건너뛰고 바로 확인 모달로 이동
    // 계좌이체 선택 시 증빙 설정 모달 표시 (세금계산서 발행 필요)

    // 업그레이드인 경우 프로레이션 계산 (FREE에서 유료 플랜으로 변경 포함)
    if (getChangeType(displayPlan, target) === 'UPGRADE') {
      try {
        const prorationData = await planApi.calculateProration(target);
        console.log('프로레이션 계산 성공:', prorationData.data);
        setPaymentMethodModal({ open: true, targetPlan: target, proration: prorationData.data });
        setPaymentProrationExpanded(false);
      } catch (err) {
        console.error('프로레이션 계산 실패:', err);
        setPaymentMethodModal({ open: true, targetPlan: target, proration: null });
        setPaymentProrationExpanded(false);
      }
    } else {
      setPaymentMethodModal({ open: true, targetPlan: target, proration: null });
      setPaymentProrationExpanded(false);
    }
  };

  const handleConfirmChange = async () => {
    if (!confirmModal.targetPlan) return;

    setChanging(true);
    setConfirmModal({ ...confirmModal, error: null });
    try {
      const targetPlan = confirmModal.targetPlan;

      // 팀 소속이면 팀 플랜 변경 API, 개인이면 개인 플랜 변경 API 호출
      const teamContext = planInfo?.isTeamContext ?? (planInfo as any)?.teamContext ?? false;
      const teamId = planInfo?.teamId ?? (aiUsageStatus?.teamId);

            if (teamContext && teamId) {
        const res = await teamApi.changeTeamPlan(teamId, targetPlan as 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE', selectedPaymentMethod);
        setConfirmModal({ open: false, targetPlan: null, error: null, proration: null });
        
        // 팀 플랜 다운그레이드 시 effectiveDate 사용하여 동적 메시지 표시
        let message = '팀 요금제 변경이 요청되었습니다.';
        if (res.data.changeType === 'DOWNGRADE' && res.data.effectiveDate) {
          const planLabel = getPlanMeta(targetPlan).name;
          message = `${planLabel} 플랜 다운그레이드가 예약되었습니다. ${res.data.effectiveDate}부터 적용됩니다.`;
        }
        
        setResultModal({ open: true, result: { success: true, message, effectiveDate: res.data.effectiveDate } as any });
        await load(); // 데이터 갱신하여 중복 요청 방지
      } else {
        const res = await planApi.changePlan({ targetPlan, paymentMethod: selectedPaymentMethod });
        setConfirmModal({ open: false, targetPlan: null, error: null, proration: null });
        
        // 사용자 경험 개선: 결제 방법에 따른 메시지 표시
        let message = res.data.message || '요금제 변경이 완료되었습니다.';
        if (res.data.changeType === 'DOWNGRADE' && res.data.effectiveDate) {
          const planLabel = getPlanMeta(targetPlan).name;
          message = `${planLabel} 플랜 다운그레이드가 예약되었습니다. ${res.data.effectiveDate}부터 적용됩니다.`;
        } else if (res.data.paymentMethod === 'CARD') {
          message = '카드 결제가 완료되었습니다. 플랜이 즉시 적용되었습니다.';
        } else if (res.data.paymentMethod === 'BANK_TRANSFER' && res.data.amount) {
          message = `결제 금액: ${res.data.amount.toLocaleString()}원. 입금 확인 후 플랜이 적용됩니다.`;
        }
        
        setResultModal({ 
          open: true, 
          result: { 
            ...res.data,
            message 
          } 
        });
        await load(); // 데이터 갱신하여 중복 요청 방지
      }
    } catch (err: any) {
      console.error('요금제 변경 에러:', err);
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message;
      // 모달을 닫지 않고 에러 메시지 표시
      let errorMsg = '요금제 변경 요청 중 오류가 발생했습니다.';
      if (status === 409) {
        // 백엔드에서 오는 에러 메시지를 그대로 표시 (다운그레이드 사용량 초과 포함)
        errorMsg = localizePlanCodeMessage(message ?? '이미 처리 중인 변경이 있습니다.');
      } else {
        errorMsg = localizePlanCodeMessage(message ?? '요금제 변경 요청 중 오류가 발생했습니다.');
      }
      setConfirmModal({ ...confirmModal, error: errorMsg });
    } finally {
      setChanging(false);
    }
  };

  const handlePaymentMethodSelect = async (method: PaymentMethod) => {
    console.log('handlePaymentMethodSelect 호출:', method, '카드 수:', cards.length);
    setSelectedPaymentMethod(method);
    const targetPlan = paymentMethodModal.targetPlan;
    if (!targetPlan) return;

    if (method === 'CARD') {
      // 카드 결제: 등록된 카드가 있으면 확인 모달 표시, 없으면 카드 등록 모달 표시
      // 카드 결제는 세금계산서 발행 불필요 (증빙 설정 모달 건너뜀)
      if (cards.length > 0) {
        // 등록된 카드가 있으면 확인 모달 표시
        console.log('카드 있음, 확인 모달 열기');
        setPaymentMethodModal({ open: false, targetPlan: null, proration: null });
        setConfirmModal({ open: true, targetPlan, error: null, proration: null });
      } else {
        // 카드가 없으면 카드 등록 모달 표시
        console.log('카드 없음, 카드 등록 모달 열기');
        setCardRegisterTargetPlan(targetPlan);  // 카드 등록 후 결제할 플랜 저장
        setPaymentMethodModal({ open: false, targetPlan: null, proration: null });
        setCardRegisterModal(true);
      }
    } else {
      // 계좌이체: 증빙 설정 모달 표시 (세금계산서 발행 필요)
      console.log('계좌이체, 증빙 설정 모달 열기');
      setPaymentMethodModal({ open: false, targetPlan: null, proration: null });
      openReceiptModal(targetPlan);
    }
  };

  /** P0-T01: PENDING_DOWNGRADE 취소 (다운그레이드 예약 철회) */
  const handleCancelPendingDowngrade = async () => {
    setCancellingDowngrade(true);
    setError(null);
    try {
      // 백엔드에서 팀/개인 컨텍스트를 자동으로 판단하여 처리
      await planApi.cancelDowngrade();
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? '다운그레이드 예약 취소 처리 중 오류가 발생했습니다.';
      setError(localizePlanCodeMessage(msg));
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const handleCancelPayment = (paymentId: number) => {
    setCancelPaymentConfirm({ open: true, paymentId });
  };

  const handleConfirmCancelPayment = async () => {
    if (!cancelPaymentConfirm.paymentId) return;
    setCancellingPayment(true);
    try {
      await planApi.cancelPayment(cancelPaymentConfirm.paymentId);
      setCancelPaymentConfirm({ open: false, paymentId: null });
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? '결제 취소 중 오류가 발생했습니다.');
    } finally {
      setCancellingPayment(false);
    }
  };

  const handleDownloadInvoice = async (paymentId: number) => {
    try {
      const response = await planApi.downloadInvoice(paymentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? '청구서 다운로드 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // 팀 컨텍스트 확인 (백엔드 응답 기준)
  const isTeamContext = planInfo?.isTeamContext ?? aiUsageStatus?.isTeamContext ?? false;
  const isTeamOwner = isTeamContext && (planInfo?.role === 'OWNER' || aiUsageStatus?.role === 'OWNER');

  // 현재 플랜 결정: 팀/개인 컨텍스트별로 올바른 필드 사용
  const currentPlan = isTeamContext
    ? (planInfo?.teamPlan ?? 'FREE' as PlanCode)
    : (planInfo?.currentPlan ?? user?.subscriptionPlan ?? 'FREE' as PlanCode);
  const currentMeta = getPlanMeta(currentPlan);

  // 업그레이드 입금 대기 확인: 팀/개인 컨텍스트별로 올바른 필드 사용
  const hasPendingUpgrade = isTeamContext
    ? (planInfo?.teamHasPendingUpgrade === true) || !!pendingPayment
    : (planInfo?.hasPendingUpgrade === true) || !!pendingPayment;

  // 사용량: 팀/개인 컨텍스트별로 올바른 필드 사용
  const usedCount = isTeamContext
    ? (usageInfo?.teamUsedCount ?? 0)
    : (usageInfo?.usedCount ?? 0);

  // 월 한도: 팀/개인 컨텍스트별로 올바른 필드 사용
  // monthlyLimit(표시용)만 personalPlanOverlay가 있으면 우선 사용 - handleChangePlan
  // 등 핸들러는 이 값이 아니라 usageInfo/planInfo를 직접 읽으므로 영향 없음.
  const monthlyLimit = isTeamContext
    ? (usageInfo?.teamMonthlyLimit ?? planInfo?.teamMonthlyLimit ?? currentMeta.monthlyLimit ?? 0)
    : (personalPlanOverlay?.monthlyLimit ?? usageInfo?.monthlyLimit ?? planInfo?.monthlyLimit ?? currentMeta.monthlyLimit ?? 0);

  // 엔터프라이즈 계약 한도 적용 (usageInfo에서 우선 가져옴, 팀/개인 컨텍스트별로 올바른 필드 사용)
  const planEnterpriseContract = isTeamContext
    ? (usageInfo?.teamEnterpriseContract ?? planInfo?.teamEnterpriseContract)
    : (usageInfo?.enterpriseContract ?? planInfo?.enterpriseContract);
  const effectiveMonthlyLimit = planEnterpriseContract
    ? (planEnterpriseContract.monthlyLimit ?? 0)
    : monthlyLimit;

  // 이번 달 사용 기간 - BE(getMyPlan)가 이미 계산해 내려주는 billingStartDate/
  // billingEndDate를 그대로 쓴다. 예전엔 billingCycleDay로 FE가 직접 재계산했는데,
  // BE 공식(다운그레이드 적용일 등)이 바뀔 때 FE가 따라가지 못해 값이 어긋날 수 있는
  // 위험이 있어(2026-08-03, computeDowngradeEffectiveDate 통일 작업 중 같은 종류의
  // 위험 발견) API 값 단일 소스로 통일한다.
  const formatShortDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getMonth() + 1}.${d.getDate()}`;
  };
  const calculatedBillingPeriod = planInfo?.billingStartDate && planInfo?.billingEndDate
    ? { startDate: formatShortDate(planInfo.billingStartDate), endDate: formatShortDate(planInfo.billingEndDate) }
    : null;

  // 사용률: 팀/개인 컨텍스트별로 올바른 필드 사용
  const usageRate = isTeamContext
    ? (usageInfo?.teamUsageRate ?? 0)
    : (usageInfo?.usageRate ?? 0);

  // 다운그레이드 변경 가능 여부: 팀/개인 컨텍스트별로 올바른 필드 사용
  const canChangePlan = isTeamContext
    ? (planInfo?.teamCanChangePlan ?? false)
    : (planInfo?.canChangePlan ?? false);

  // 다운그레이드 가능 여부(표시용): 팀/개인 컨텍스트별로 올바른 필드 사용.
  // personalPlanOverlay가 있으면 우선 사용 - handleChangePlan은 이 const가 아니라
  // planInfo?.canDowngrade를 직접 다시 읽으므로(위 workspacePlanOverlay.ts 주석 참고)
  // 실제 변경 요청 차단 로직에는 영향 없음.
  const canDowngrade = isTeamContext
    ? (planInfo?.teamCanDowngrade ?? false)
    : (personalPlanOverlay?.canDowngrade ?? planInfo?.canDowngrade ?? false);

  // 다운그레이드 불가 사유(표시용): 팀/개인 컨텍스트별로 올바른 필드 사용
  const downgradeReason = isTeamContext
    ? (planInfo?.teamDowngradeReason ?? null)
    : (personalPlanOverlay?.downgradeReason ?? planInfo?.downgradeReason ?? null);

  // 일반 플랜 변경 자체가 막히는 사유(예: 엔터프라이즈 계약 활성 중, 표시용) - 팀/개인 컨텍스트별로 올바른 필드 사용
  const changePlanBlockReason = isTeamContext
    ? (planInfo?.teamChangePlanReason ?? null)
    : (personalPlanOverlay?.changePlanReason ?? planInfo?.changePlanReason ?? null);

  // 다운그레이드 예약 플랜: 팀/개인 컨텍스트별로 올바른 필드 사용
  const pendingDowngradePlan = isTeamContext
    ? (planInfo?.teamPendingPlan ?? null)
    : (planInfo?.pendingPlan ?? null);

  // 다운그레이드 예약된 적용일: 팀/개인 컨텍스트별로 올바른 필드 사용
  const downgradeEffectiveDate = isTeamContext
    ? (planInfo?.teamDowngradeEffectiveDate ?? null)
    : (planInfo?.downgradeEffectiveDate ?? null);

  // 소급청구(미납분) 여부 - src/lib/planUtils.ts의 isBackBillPending 참고
  const pendingIsBackBill = isBackBillPending(pendingPayment, currentPlan);

  // 대기 중인 변경의 방향 라벨: pendingDowngradePlan(User/Team의 라이브 필드)이 없어도
  // pendingPayment 자체가 다운그레이드 예약건(isDowngrade)일 수 있어 함께 확인한다.
  // (예: 환불로 pendingPlan은 지워졌지만 정리되지 않은 다운그레이드 예약 결제 건이 남아있던 경우 방어)
  const pendingChangeLabel = (pendingDowngradePlan || pendingPayment?.isDowngrade)
    ? '다운그레이드'
    : pendingIsBackBill
    ? '미납요금'
    : '업그레이드';

  // 대기 중인 변경이 있어 추가 변경이 막혔을 때 보여줄 상세 사유 (어느 플랜에서 어느 플랜으로 가는 중인지 명시)
  const pendingChangeTargetPlan = pendingDowngradePlan ?? pendingPayment?.plan ?? null;
  const pendingChangeDetailMessage = pendingIsBackBill
    ? `${getPlanMeta(currentPlan).name} 요금제 미납 요금이 입금 대기 중이라 추가로 요금제를 조정할 수 없습니다.`
    : pendingChangeTargetPlan
    ? `${getPlanMeta(currentPlan).name} 요금제에서 ${getPlanMeta(pendingChangeTargetPlan).name} 요금제로 ${pendingChangeLabel} 신청이 되어 추가로 요금제를 조정할 수 없습니다.`
    : `입금 대기 중인 ${pendingChangeLabel} 요청이 있어 추가로 요금제를 조정할 수 없습니다.`;

  // 다운그레이드 예상 적용일: 팀/개인 컨텍스트별로 올바른 필드 사용
  const estimatedDowngradeEffectiveDate = isTeamContext
    ? (planInfo?.teamEstimatedDowngradeEffectiveDate ?? null)
    : (planInfo?.estimatedDowngradeEffectiveDate ?? null);

  // 백엔드가 이미 컨텍스트에 따라 올바른 currentPlan을 반환하므로 단순화
  const displayPlan = currentPlan;
  const displayMeta = getPlanMeta(displayPlan);
  const displayPlanName = currentPlan;

  // P0-T01: subscriptionStatus 확정
  // /api/my/plan 응답의 subscriptionStatus 필드 우선, 없으면 기존 필드로 추론
  const subscriptionStatus: SubscriptionStatus = planInfo?.subscriptionStatus
    ?? deriveSubscriptionStatus(planInfo ?? { currentPlan: 'FREE', pendingPlan: null, hasPendingUpgrade: false } as PlanInfo, hasPendingUpgrade) as SubscriptionStatus;
  const statusMeta = SUBSCRIPTION_STATUS_META[subscriptionStatus];

  // 입금 대기 상태 확인 (일반 플랜 + 엔터프라이즈)
  const isEnterprisePending = planEnterpriseContract?.status === 'PENDING' || planEnterpriseContract?.isDowngrade === true;
  const isNormalPlanPending = subscriptionStatus === 'PENDING_UPGRADE' || subscriptionStatus === 'PENDING_DOWNGRADE';
  const isAnyPaymentPending = isEnterprisePending || isNormalPlanPending;

  // 다운그레이드 취소 버튼 표시 조건: pendingPlan이 있거나, PENDING 결제 이력의 플랜이 현재 플랜보다 낮은 경우 (다운그레이드)
  const isPendingDowngrade = pendingPayment && (
    (currentPlan === 'PROFESSIONAL' && pendingPayment.plan === 'STARTER') ||
    (currentPlan === 'PROFESSIONAL' && pendingPayment.plan === 'FREE') ||
    (currentPlan === 'STARTER' && pendingPayment.plan === 'FREE')
  );
  const shouldShowDowngradeCancelButton = (pendingDowngradePlan !== null) || isPendingDowngrade || (planEnterpriseContract?.isDowngrade === true);

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="요금제 관리"
        description="현재 이용 중인 요금제를 확인하고 변경할 수 있습니다."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5"
              onClick={() => navigate('/dashboard/my/subscription-history')}
            >
              <History className="w-4 h-4" />
              상태 변경 이력
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 결제 실패/정지로 AI 생성이 제한된 경우 안내 배너 */}
      {aiUsageStatus?.suspended && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
          <AlertCircle className="w-5 h-5 shrink-0 text-red-600" />
          <span className="flex-1 text-sm font-medium">
            {isTeamContext
              ? '결제가 확인되지 않아 AI 생성이 제한되었습니다. 계좌이체를 이용하셨다면 관리자 입금 확인 후 자동으로 정상화됩니다. 카드 결제가 실패했다면 아래에서 결제 수단을 확인하거나 팀 관리자에게 문의해 주세요.'
              : '결제가 확인되지 않아 AI 생성이 제한되었습니다. 계좌이체를 이용하셨다면 관리자 입금 확인 후 자동으로 정상화됩니다. 카드 결제가 실패했다면 아래에서 결제 수단을 확인해 주세요.'}
          </span>
        </div>
      )}

      {/* P0-T01: 구독 상태 배지 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500">구독 상태:</span>
        <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
        {subscriptionStatus === 'PENDING_UPGRADE' && (
          <div className="text-xs text-blue-600">
            <p>입금 확인이 완료되면 자동으로 플랜이 적용됩니다. 미입금 시 24시간 후 자동 취소됩니다.</p>
            {pendingPayment && pendingPayment.amount > 0 && (
              <p className="mt-1">
                공급가액: ₩{Math.round(pendingPayment.amount / 1.1).toLocaleString()}, 부가세: ₩{Math.round(pendingPayment.amount / 11).toLocaleString()}
              </p>
            )}
            {pendingPayment && (
              <Button
                variant="link"
                className="text-xs p-0 h-auto text-blue-600 hover:text-blue-800 mt-1"
                onClick={() => handleDownloadInvoice(pendingPayment.id)}
              >
                청구서 다운로드
              </Button>
            )}
          </div>
        )}
        {subscriptionStatus === 'ACTIVE_PAID' && latestSuccessfulPayment && (
          <div className="flex gap-2">
            <Button
              variant="link"
              className="text-xs p-0 h-auto text-blue-600 hover:text-blue-800"
              onClick={() => handleDownloadInvoice(latestSuccessfulPayment.id)}
            >
              청구서 다운로드
            </Button>
          </div>
        )}
        {subscriptionStatus === 'PENDING_DOWNGRADE' && pendingDowngradePlan && downgradeEffectiveDate && (
          <span className="text-xs text-amber-700">
            {new Date(downgradeEffectiveDate).toLocaleDateString('ko-KR')} 에 {getPlanMeta(pendingDowngradePlan).name} 플랜으로 전환 예정
          </span>
        )}
      </div>

      {/* P0-T01: PENDING_DOWNGRADE 취소 버튼 */}
      {shouldShowDowngradeCancelButton && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-amber-800 text-sm">
              {downgradeEffectiveDate
                ? `${new Date(downgradeEffectiveDate).toLocaleDateString('ko-KR')} 에 ${pendingDowngradePlan ? getPlanMeta(pendingDowngradePlan).name : '다운그레이드'} 플랜으로 전환됩니다.`
                : `${pendingDowngradePlan ? getPlanMeta(pendingDowngradePlan).name : '다운그레이드'} 플랜으로 다운그레이드가 예약되어 있습니다.`}
              {' '}
              {downgradeEffectiveDate
                ? `${new Date(new Date(downgradeEffectiveDate).getTime() - 86400000).toLocaleDateString('ko-KR')}까지는 현재 플랜 혜택이 유지됩니다.`
                : '당월 말까지는 현재 플랜 혜택이 유지됩니다.'}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
              disabled={cancellingDowngrade}
              onClick={handleCancelPendingDowngrade}
            >
              {cancellingDowngrade
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />취소 중...</>
                : <><XCircle className="w-3.5 h-3.5 mr-1" />다운그레이드 예약 취소</>}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* 현재 요금제 요약 카드 */}
      <Card className={`border-2 ${currentMeta.borderColor} ${currentMeta.bgColor}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap className={`w-5 h-5 ${currentMeta.color}`} />
              <CardTitle className={`text-lg ${currentMeta.color}`}>현재 플랜: {displayPlanName}</CardTitle>
            </div>
            {pendingDowngradePlan && (
              <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300">
                다음 달 {getPlanMeta(pendingDowngradePlan).name} 전환 예정
              </Badge>
            )}
            {hasPendingUpgrade && (
              <Badge className="bg-blue-100 text-blue-700 border border-blue-300">
                {pendingPayment
                  ? `${pendingChangeLabel} 입금대기: ${getPlanMeta(pendingPayment.plan).name}`
                  : `${pendingChangeLabel} 입금대기 진행중`}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 엔터프라이즈 계약 정보 */}
          {currentPlan === 'ENTERPRISE' && planEnterpriseContract && (
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-purple-600" />
                <span className="font-semibold text-purple-800">엔터프라이즈 계약 정보</span>
              </div>
              <div className="text-xs text-purple-700 space-y-1">
                <p>계약명: {planEnterpriseContract.contractName}</p>
                <p>월 사용량: {planEnterpriseContract.monthlyLimit.toLocaleString()}건</p>
                <p>월 이용료: ₩{planEnterpriseContract.monthlyPrice.toLocaleString()}</p>
                {planEnterpriseContract.monthlyPrice > 0 && (
                  <p className="text-gray-500"> (공급가액: ₩{Math.round(planEnterpriseContract.monthlyPrice / 1.1).toLocaleString()}, 부가세: ₩{Math.round(planEnterpriseContract.monthlyPrice / 11).toLocaleString()})</p>
                )}
                <p>계약 기간: {new Date(planEnterpriseContract.startDate).toLocaleDateString('ko-KR')} ~ {new Date(planEnterpriseContract.endDate).toLocaleDateString('ko-KR')}</p>
                <p>자동 갱신: {planEnterpriseContract.autoRenewal ? '활성' : '비활성'}</p>
                <p>
                  상태: {
                    planEnterpriseContract.status === 'ACTIVE' ? '활성'
                    : planEnterpriseContract.status === 'PENDING' ? '입금 대기'
                    : planEnterpriseContract.status === 'SUSPENDED' ? '정지 (AI 사용 차단)'
                    : planEnterpriseContract.status === 'EXPIRED' ? '만료'
                    : '취소'
                  }
                </p>
                {planEnterpriseContract.status === 'SUSPENDED' && (
                  <p className="text-red-600 mt-1">
                    정기결제 실패로 계약이 정지되었습니다. AI 생성이 제한됩니다. 결제 수단을 확인해 주세요.
                  </p>
                )}
                {/* 다운그레이드 정보 표시 */}
                {planEnterpriseContract.isDowngrade && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-amber-800 font-semibold">다운그레이드 예약</p>
                    <p className="text-amber-700">{planEnterpriseContract.downgradeMessage}</p>
                    {planEnterpriseContract.downgradeEffectiveDate && (
                      <p className="text-amber-700">적용일: {new Date(planEnterpriseContract.downgradeEffectiveDate).toLocaleDateString('ko-KR')}</p>
                    )}
                    {planEnterpriseContract.nextBillingAmount && (
                      <p className="text-amber-700">다음 청구 금액: ₩{planEnterpriseContract.nextBillingAmount.toLocaleString()}</p>
                    )}
                  </div>
                )}
                {!(isTeamContext && !isTeamOwner) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                    disabled={isAnyPaymentPending || planEnterpriseContract.isDowngrade}
                    title={isAnyPaymentPending ? '입금 대기 중입니다.' : planEnterpriseContract.isDowngrade ? '다운그레이드 예약 중입니다.' : undefined}
                    onClick={() => setEnterpriseModalOpen(true)}
                  >
                    계약 수정
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* 팀 컨텍스트 정보 */}
          {isTeamContext && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-blue-800">팀: {aiUsageStatus?.teamName}</span>
              </div>
              <div className="text-xs text-blue-700 space-y-1">
                <p>역할: {aiUsageStatus?.role === 'OWNER' ? '팀장' : '팀원'}</p>
                <p>팀 요금제: {displayPlanName}</p>
                {planInfo?.billingStartDate && planInfo?.billingEndDate && (
                  <p>팀 이번 달 사용 기간: {new Date(planInfo.billingStartDate).toLocaleDateString('ko-KR')} ~ {new Date(planInfo.billingEndDate).toLocaleDateString('ko-KR')}</p>
                )}
              </div>
            </div>
          )}

          {/* 이용 기간 (개인 컨텍스트만) */}
          {!isTeamContext && billingPeriod && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="font-semibold text-gray-800">이용 기간</span>
              </div>
              <div className="text-xs text-gray-700 space-y-1">
                <p>이용 기간: {formatDateWithDay(billingPeriod.startDate)} ~ {formatDateWithDay(billingPeriod.endDate)}</p>
                <p>다음 결제일: {new Date(billingPeriod.nextBillingDate).toLocaleDateString('ko-KR')}</p>
              </div>
            </div>
          )}

          {/* 사용량 프로그레스 바 */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">
                {isTeamContext && aiUsageStatus?.teamTotalUsed !== null
                  ? '팀 전체 사용량'
                  : calculatedBillingPeriod
                    ? `${calculatedBillingPeriod.startDate}~${calculatedBillingPeriod.endDate} AI 사용`
                    : '이번 달 AI 사용량'}
              </span>
              <span className="font-semibold">
                {isTeamContext && aiUsageStatus?.teamTotalUsed !== null
                  ? `${aiUsageStatus?.teamTotalUsed?.toLocaleString() ?? 0} / ${effectiveMonthlyLimit > 0 ? effectiveMonthlyLimit.toLocaleString() : '무제한'}건`
                  : `${usedCount.toLocaleString()} / ${effectiveMonthlyLimit > 0 ? effectiveMonthlyLimit.toLocaleString() : '무제한'}건`}
                {effectiveMonthlyLimit > 0 && <span className="text-gray-400 ml-1">({Math.round(usageRate)}%)</span>}
              </span>
            </div>
            {(personalPlanOverlay?.billingCycleDay ?? planInfo?.billingCycleDay) && !isTeamContext && (
              <div className="text-xs text-gray-500 mt-1">
                청구 주기일: {personalPlanOverlay?.billingCycleDay ?? planInfo?.billingCycleDay}일
              </div>
            )}
            {monthlyLimit > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    usageRate >= 100 ? 'bg-red-500' : usageRate >= 80 ? 'bg-orange-400' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(usageRate, 100)}%` }}
                />
              </div>
            )}
            {isTeamContext && aiUsageStatus?.teamTotalUsed !== null && (
              <p className="text-xs text-gray-500 mt-1">본인 사용량: {usedCount.toLocaleString()}건</p>
            )}
            {usageRate >= 80 && usageRate < 100 && (
              <p className="text-xs text-orange-600 mt-1">사용량이 80%를 초과했습니다. 상위 플랜 업그레이드를 고려해보세요.</p>
            )}
            {usageRate >= 100 && (
              <p className="text-xs text-red-600 mt-1">이번 달 사용량을 모두 소진했습니다. 업그레이드 후 계속 이용 가능합니다.</p>
            )}
          </div>

          {/* 하단 중복 UI - hidden */}
          {/*
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            {usageInfo?.planExpiryDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                다음 갱신일: {new Date(usageInfo.planExpiryDate).toLocaleDateString('ko-KR')}
              </span>
            )}
            {!isTeamContext && usageInfo?.billingStartDate && usageInfo?.billingEndDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                이번 달 사용 기간: {new Date(usageInfo.billingStartDate).toLocaleDateString('ko-KR')} ~ {new Date(usageInfo.billingEndDate).toLocaleDateString('ko-KR')}
              </span>
            )}
            {!canDowngrade && downgradeReason && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-3.5 h-3.5" />
                {getDowngradeMessage(downgradeReason)}
              </span>
            )}
            {hasPendingUpgrade && (
              <span className="flex items-center gap-1 text-blue-600">
                <AlertCircle className="w-3.5 h-3.5" />
                {pendingPayment ? (
                  <div className="flex flex-col">
                    <span>
                      {pendingIsBackBill
                        ? `${getPlanMeta(pendingPayment.plan).name} 플랜 미납 요금이 입금대기 중입니다. (청구일: ${new Date(pendingPayment.createdAt).toLocaleDateString('ko-KR')})`
                        : `${getPlanMeta(pendingPayment.plan).name} 플랜 업그레이드 요청이 입금대기 중입니다. (요청일: ${new Date(pendingPayment.createdAt).toLocaleDateString('ko-KR')})`}
                    </span>
                    <span className="text-xs">
                      금액: ₩{pendingPayment.amount.toLocaleString()}
                      {pendingPayment.amount > 0 && (
                        <span className="text-gray-500"> (공급가액: ₩{Math.round(pendingPayment.amount / 1.1).toLocaleString()}, 부가세: ₩{Math.round(pendingPayment.amount / 11).toLocaleString()})</span>
                      )}
                    </span>
                  </div>
                ) : '입금 대기 중인 업그레이드 요청이 있습니다.'}
                <div className="flex gap-2 ml-2">
                  {pendingPayment?.canCancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleCancelPayment(pendingPayment.id)}
                    >
                      취소
                    </Button>
                  )}
                  {pendingPayment && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => handleDownloadInvoice(pendingPayment.id)}
                    >
                      청구서
                    </Button>
                  )}
                </div>
              </span>
            )}
          </div>
          */}
        </CardContent>
      </Card>

      {/* 등록된 카드 섹션 - hidden */}
      {false && cards.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              등록된 카드
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cards.map((card) => (
                <div key={card.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-800">{card.cardCompany}</div>
                      <div className="text-sm text-gray-500">{card.cardNumberMasked}</div>
                    </div>
                    {card.isDefault && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">기본</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCardDelete(card.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 요금제 비교 카드 */}
      <div>
        <h2 className="text-base font-semibold text-gray-700 mb-3">요금제 비교</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map(plan => {
            const isCurrent = currentPlan === plan.code;
            const changeType = !isCurrent && plan.code !== 'ENTERPRISE'
              ? getChangeType(currentPlan as PlanCode, plan.code)
              : null;
            // P0-T01: subscriptionStatus 기반 버튼 활성 여부
            const canUpgrade = subscriptionStatus !== 'PENDING_UPGRADE' && !hasPendingUpgrade;

            return (
              <Card
                key={plan.code}
                className={`relative border-2 transition-shadow ${
                  isCurrent ? `${plan.borderColor} shadow-md` : 'border-gray-200 hover:shadow-md'
                }`}
              >
                {/* 배지 */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1">
                  {isCurrent && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 text-white font-semibold whitespace-nowrap">
                      현재 플랜
                    </span>
                  )}
                  {plan.badge && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${plan.badgeColor}`}>
                      {plan.badge}
                    </span>
                  )}
                </div>

                <CardHeader className="pt-6 pb-3 text-center">
                  <p className={`text-base font-bold ${plan.color}`}>{plan.name}</p>
                  <div className="mt-1">
                    {plan.price === null ? (
                      <p className="text-2xl font-bold text-gray-800">문의</p>
                    ) : plan.price === 0 ? (
                      <p className="text-2xl font-bold text-gray-800">무료</p>
                    ) : (
                      <p className="text-2xl font-bold text-gray-800">
                        ₩{plan.price.toLocaleString()}
                        <span className="text-sm font-normal text-gray-500">/월</span>
                        <span className="text-xs text-gray-400 ml-1">(부가세 포함)</span>
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {plan.monthlyLimit !== null ? `월 ${plan.monthlyLimit.toLocaleString()}건` : '무제한'}
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 space-y-3">
                  <ul className="space-y-1.5">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* 변경 버튼 */}
                  {isCurrent ? (
                    <Button className="w-full" disabled size="sm">현재 이용 중</Button>
                  ) : isTeamContext && !isTeamOwner ? (
                    <DisabledHint title="팀 요금제 변경은 팀장만 가능합니다.">
                      <Button className="w-full" variant="outline" size="sm" disabled>
                        팀장만 변경 가능
                      </Button>
                    </DisabledHint>
                  ) : changeType === 'DOWNGRADE' && !canDowngrade ? (
                    <DisabledHint title={downgradeReason ? getDowngradeMessage(downgradeReason) : '다운그레이드가 불가능합니다'}>
                      <Button className="w-full" variant="outline" size="sm" disabled>
                        다운그레이드 불가
                      </Button>
                    </DisabledHint>
                  ) : plan.code === 'ENTERPRISE' ? (
                    <DisabledHint title={hasPendingUpgrade ? pendingChangeDetailMessage : undefined}>
                      <Button
                        className="w-full bg-brand-purple hover:bg-brand-purple-light text-white"
                        size="sm"
                        disabled={hasPendingUpgrade}
                        onClick={() => handleChangePlan('ENTERPRISE')}
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1" />문의하기
                      </Button>
                    </DisabledHint>
                  ) : changeType === 'UPGRADE' ? (
                    <DisabledHint title={(isAnyPaymentPending || !canUpgrade) ? pendingChangeDetailMessage : undefined}>
                      <Button
                        className="w-full"
                        size="sm"
                        disabled={!canUpgrade || isAnyPaymentPending}
                        onClick={() => handleChangePlan(plan.code)}
                      >
                        <TrendingUp className="w-3.5 h-3.5 mr-1" />업그레이드
                      </Button>
                    </DisabledHint>
                  ) : isTeamContext && plan.code === 'FREE' ? (
                    <DisabledHint title="팀 요금제는 무료 플랜으로 다운그레이드할 수 없습니다.">
                      <Button className="w-full" variant="outline" size="sm" disabled>
                        <TrendingDown className="w-3.5 h-3.5 mr-1" />다운그레이드 불가
                      </Button>
                    </DisabledHint>
                  ) : (
                    <DisabledHint
                      title={
                        changePlanBlockReason === 'ENTERPRISE_ACTIVE'
                          ? '엔터프라이즈 계약이 활성 상태입니다. 계약 만료 후 일반 플랜으로 변경할 수 있습니다.'
                          : planEnterpriseContract?.isDowngrade === true
                            ? '다운그레이드 예약 중입니다.'
                            : isAnyPaymentPending
                              ? pendingChangeDetailMessage
                              : !canDowngrade
                                ? '이번 달 다운그레이드는 1회만 가능합니다.'
                                : undefined
                      }
                    >
                      <Button
                        className="w-full"
                        variant="outline"
                        size="sm"
                        disabled={!canDowngrade || isAnyPaymentPending || planEnterpriseContract?.isDowngrade === true || changePlanBlockReason === 'ENTERPRISE_ACTIVE'}
                        onClick={() => handleChangePlan(plan.code)}
                      >
                        <TrendingDown className="w-3.5 h-3.5 mr-1" />다운그레이드
                      </Button>
                    </DisabledHint>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* 요금제 정책 안내 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            요금제 정책 안내
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <Tabs value={policyTab} onValueChange={setPolicyTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
              <TabsTrigger value="personal">개인 요금제 정책</TabsTrigger>
              <TabsTrigger value="refund">환불 정책</TabsTrigger>
              <TabsTrigger value="team">팀 요금제 정책</TabsTrigger>
              <TabsTrigger value="limits">요금제 한도</TabsTrigger>
            </TabsList>

            {/* 개인 요금제 정책 */}
            <TabsContent value="personal" className="space-y-4 mt-4">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <AlertDescription className="text-xs text-orange-800">
                  <span className="font-semibold">한도를 초과하면 추가 요금 없이 이용이 즉시 제한됩니다.</span> 이번 달 한도(또는 무료 플랜의 일일 한도)를 모두 사용하면, 다음 결제 주기(또는 다음 날)가 시작되기 전까지 새로운 ALT 텍스트 생성 요청 자체가 거부됩니다.
                </AlertDescription>
              </Alert>
              <div className="space-y-3 text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">요금제 변경 시점</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• <span className="font-medium">업그레이드(카드 결제):</span> 결제 승인 즉시 자동으로 플랜 변경</li>
                    <li>• <span className="font-medium">업그레이드(계좌이체):</span> 관리자 입금 확인 후 플랜 변경</li>
                    <li>• <span className="font-medium">다운그레이드:</span> {getDowngradePolicyCardText(nonEnterpriseDowngradeAppliesFirstOfMonth)}</li>
                    <li>• <span className="font-medium">취소:</span> 즉시 무료 플랜으로 변경</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">요금제 변경 제한</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 업그레이드: 횟수 제한 없음 (단, 입금 대기 중인 요청이 있으면 추가 요청 불가)</li>
                    <li>• 다운그레이드: 최근 30일 이내 1회 제한 (당월 사용량이 목표 플랜 한도 초과 시 불가)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">이번 달 사용 기간</p>
                  <p className="text-xs mt-1">첫 결제일을 기준으로 매월 같은 날짜에 갱신됩니다. (예: 첫 결제일이 3월 28일이면 사용 기간은 5월 29일 ~ 6월 29일)</p>
                  <p className="text-xs mt-1">이용 주기 기준일에 따라 사용 기간이 결정됩니다.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">결제 방식 및 승인 절차</p>
                  <div className="mt-1 space-y-2 text-xs">
                    <div>
                      <p className="font-medium">결제 방식</p>
                      <ul className="mt-1 space-y-1">
                        <li>• 계좌이체: 무통장 입금</li>
                        <li>• 카드 결제: 카드 결제</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium">승인 절차</p>
                      <ul className="mt-1 space-y-1">
                        <li>• <span className="font-medium">계좌이체:</span> 결제 요청 → 입금 대기 상태 → 사용자 입금 → 관리자 입금 확인 → 승인 완료 → 플랜 즉시 변경 → 증빙서류 자동 발행</li>
                        <li>• <span className="font-medium">카드 결제:</span> 카드 결제 승인 → 즉시 승인 완료 → 플랜 즉시 변경 → 증빙서류 자동 발행</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-700">카드 자동결제(정기결제) 안내</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 카드로 결제하면 이후 매 결제 주기(월 1회, 최초 결제일 기준 매월 같은 날)마다 등록된 카드로 자동 청구됩니다</li>
                    <li>• 자동결제 예정일 전에 이메일로 사전 안내를 보내드립니다</li>
                    <li>• 자동결제를 멈추려면 무료 플랜으로 변경하거나 등록된 카드를 삭제하면 됩니다 (처리 시점 이후 결제 주기부터 청구되지 않음)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">입금 대기 상태</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 입금 대기 중인 결제가 있으면 추가 업그레이드 요청 불가</li>
                    <li>• <span className="font-medium">계좌이체 요청 후 24시간 이내에 입금이 확인되지 않으면 결제가 자동으로 취소됩니다.</span> 무료 플랜에서 신규로 업그레이드를 요청한 경우 무료 플랜으로 자동 복귀합니다 (이 경우 별도 안내 메일은 발송되지 않습니다)</li>
                    <li>• 관리자가 입금을 확인하면 플랜이 즉시 변경됩니다</li>
                    <li>• 입금 대기 중인 결제가 있으면 회원 탈퇴도 제한됩니다</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">회원 탈퇴 시 처리</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 탈퇴 즉시 무료 플랜으로 전환되며, 등록된 카드의 자동결제도 함께 해지됩니다 (잔여 이용 기간에 대한 환불은 제공되지 않습니다)</li>
                    <li>• 탈퇴 후 1년간 계정 데이터가 보존되며, 이 기간 내에는 탈퇴를 취소하고 계정을 복구할 수 있습니다 (단, 카드 결제 정보·요금제·팀 소속은 복구되지 않으며 다시 설정해야 합니다)</li>
                    <li>• 탈퇴 후 1년이 지나면 모든 데이터가 자동 삭제되며 복구할 수 없습니다</li>
                    <li>• 동일한 이메일로는 탈퇴 후 30일이 지나야 재가입할 수 있습니다</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* 환불 정책 */}
            <TabsContent value="refund" className="space-y-4 mt-4">
              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-800">
                  <span className="font-semibold">환불은 결제일로부터 7일 이내이면서, 서비스를 한 번도 이용하지 않은 경우에만</span> 가능합니다. 조건을 벗어난 경우에는 환불 대신 요금제 변경(다운그레이드)을 이용해 주세요.
                </AlertDescription>
              </Alert>
              <div className="space-y-3 text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">환불 가능 기간</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 결제일로부터 7일 이내이면서 서비스를 이용한 이력이 없는 경우에만 환불 가능</li>
                    <li>• 7일 경과 또는 1회라도 서비스를 이용한 경우 환불 불가</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">환불 금액</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 환불 가능 조건을 충족하면 결제 금액 전액 환불</li>
                    <li>• 환불 가능 기간이 지났거나 이용 이력이 있으면 환불 대신 요금제 변경(다운그레이드)을 이용해 주세요 — 이번 결제 기간이 끝날 때까지는 계속 이용하실 수 있고, 이후 자동으로 전환됩니다</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">환불 절차 및 처리 기한</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 환불 요청 → 환불 가능 여부 확인(7일 이내 + 미사용) → 결제 취소 처리 → 증빙서류 취소 → 즉시 무료 플랜으로 변경</li>
                    <li>• 환불 문의는 이메일로 접수하며, 접수일로부터 3영업일 이내에 처리됩니다</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">환불 제한</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 본인의 결제만 취소 가능</li>
                    <li>• 이미 취소된 결제는 재취소 불가</li>
                    <li>• 환불 후 즉시 무료 플랜으로 변경</li>
                    <li>• 회원 탈퇴 시에는 환불이 제공되지 않습니다 — 탈퇴 전에 환불 가능 여부를 먼저 확인해 주세요</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* 팀 요금제 정책 */}
            <TabsContent value="team" className="space-y-4 mt-4">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <AlertDescription className="text-xs text-orange-800">
                  팀도 개인과 동일하게 <span className="font-semibold">한도를 초과하면 팀 전체의 이용이 즉시 제한</span>됩니다.
                </AlertDescription>
              </Alert>
              <div className="space-y-3 text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">팀 요금제 결정</p>
                  <p className="text-xs mt-1">팀 요금제는 팀장의 개인 요금제와 별개로 관리됩니다. 팀장이 팀 설정에서 요금제를 변경하면 팀 전체에 적용됩니다.</p>
                  <p className="text-xs mt-1">팀원은 개별적으로 요금제를 변경할 수 없습니다.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">팀 이번 달 사용 기간</p>
                  <p className="text-xs mt-1">팀의 사용 기간은 팀장의 사용 기간을 따릅니다. 팀장의 첫 결제일 기준으로 계산됩니다.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">팀 사용량 집계</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 팀 전체 사용량: 팀원들의 사용량을 모두 합산 (팀장만 확인 가능)</li>
                    <li>• 팀원별 사용량: 팀원은 본인 사용량만 확인</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">팀 가입 · 나가기 · 삭제</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 팀 가입: 팀 요금제/이번 달 사용 기간 적용</li>
                    <li>• 팀 나가기: 개인 요금제로 복귀 (그동안 생성한 ALT 텍스트 이력은 팀에 그대로 남습니다)</li>
                    <li>• 팀장은 탈퇴하기 전 팀장 권한을 다른 팀원에게 반드시 이전해야 합니다</li>
                    <li>• 팀이 삭제되면 팀 요금제 이용이 즉시 종료되며, 잔여 이용 기간에 대한 환불은 제공되지 않습니다</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">팀 계좌이체 결제 미입금 시</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• 계좌이체 요청 후 24시간 이내에 입금이 확인되지 않으면 결제가 자동 취소되고, 팀 이용이 일시 제한됩니다</li>
                    <li>• 이용 제한은 입금이 확인되는 즉시 해제됩니다</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* 요금제 한도 */}
            <TabsContent value="limits" className="mt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 text-left font-medium text-gray-700">플랜</th>
                      <th className="py-2 text-left font-medium text-gray-700">월 한도</th>
                      <th className="py-2 text-left font-medium text-gray-700">일일 한도</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 text-gray-800">무료 (FREE)</td>
                      <td className="py-2 text-gray-600">월 60건</td>
                      <td className="py-2 text-gray-600">일일 20건</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 text-gray-800">스타트 (STARTER)</td>
                      <td className="py-2 text-gray-600">월 1,000건</td>
                      <td className="py-2 text-gray-600">제한 없음</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 text-gray-800">프로 (PROFESSIONAL)</td>
                      <td className="py-2 text-gray-600">월 5,000건</td>
                      <td className="py-2 text-gray-600">제한 없음</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-800">엔터프라이즈 (ENTERPRISE)</td>
                      <td className="py-2 text-gray-600">사용량 합의</td>
                      <td className="py-2 text-gray-600">제한 없음</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Alert className="border-orange-200 bg-orange-50 mt-3">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <AlertDescription className="text-xs text-orange-800">
                  월 한도(그리고 무료 플랜의 일일 한도)를 초과하면 <span className="font-semibold">추가 요금이 청구되지 않고, 대신 새 ALT 텍스트 생성 요청 자체가 거부됩니다.</span> 계속 이용하시려면 다음 결제 주기를 기다리거나 상위 플랜으로 업그레이드해 주세요.
                </AlertDescription>
              </Alert>
              <p className="text-xs text-gray-500 mt-3">
                엔터프라이즈 표준 계약 기간은 신청일로부터 1년이며, 자동갱신 설정에 따라 매년 갱신됩니다. 별도로 협의된 계약서가 있는 경우 그 내용이 이 페이지보다 우선 적용됩니다.
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* 업크레이드 Upsell CTA (사용량 80% 이상) */}
      {usageRate >= 80 && currentPlan !== 'PROFESSIONAL' && currentPlan !== 'ENTERPRISE' && (
        <Card className="border-brand-orange-light bg-orange-50">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-orange-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-orange-800">더 많은 AI 생성이 필요하신가요?</p>
                <p className="text-xs text-orange-600 mt-0.5">
                  {currentPlan === 'FREE'
                    ? '스타트 플랜으로 업그레이드하면 월 500건까지 사용 가능합니다.'
                    : '프로 플랜으로 업그레이드하면 월 2,000건까지 사용 가능합니다.'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="whitespace-nowrap"
              onClick={() => handleChangePlan(currentPlan === 'FREE' ? 'STARTER' : 'PROFESSIONAL')}
            >
              업그레이드
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 증빙 선택 Modal */}
      <Dialog open={receiptModal.open} onOpenChange={(o) => !receiptSaving && setReceiptModal({ open: o, targetPlan: receiptModal.targetPlan })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              증빙 신청 설정
            </DialogTitle>
            <DialogDescription>
              입금 확인 시 자동 발행할 증빙 유형을 선택하세요.
              {receiptHasExisting && ' 기존 설정을 불러왔습니다.'}
            </DialogDescription>
          </DialogHeader>

          {receiptLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-5">
              {receiptError && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{receiptError}</AlertDescription>
                </Alert>
              )}

              {/* 유형 선택 */}
              <div className="grid grid-cols-3 gap-3">
                {([
                  { type: 'CASH_RECEIPT' as ReceiptType, icon: Receipt, label: '현금영수증', sub: '개인 · 사업자' },
                  { type: 'TAX_INVOICE' as ReceiptType, icon: Building2, label: '세금계산서', sub: '사업자 전용' },
                  { type: 'NONE' as ReceiptType, icon: BanIcon, label: '발행 안함', sub: undefined },
                ]).map(({ type, icon: Icon, label, sub }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { setReceiptForm(prev => ({ ...prev, receiptType: type })); setReceiptError(null); }}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 py-4 px-3 text-sm font-medium transition-all ${
                      receiptForm.receiptType === type
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${receiptForm.receiptType === type ? 'text-primary' : 'text-gray-400'}`} />
                    <span>{label}</span>
                    {sub && <span className="text-[11px] text-gray-400 font-normal">{sub}</span>}
                  </button>
                ))}
              </div>

              {/* 현금영수증 */}
              {receiptForm.receiptType === 'CASH_RECEIPT' && (
                <div className="space-y-4 pt-2 border-t">
                  <p className="text-sm font-medium text-gray-700">용도 선택</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {([
                      { purpose: 'PERSONAL_DEDUCTION' as CashReceiptPurpose, label: '개인 소득공제용', sub: '휴대폰번호 입력' },
                      { purpose: 'BUSINESS_EXPENSE' as CashReceiptPurpose, label: '사업자 지출증빙용', sub: '사업자번호 입력' },
                    ]).map(({ purpose, label, sub }) => (
                      <button
                        key={purpose}
                        type="button"
                        onClick={() => setReceiptForm(prev => ({ ...prev, cashReceiptPurpose: purpose }))}
                        className={`flex items-center gap-3 rounded-lg border-2 py-3 px-4 text-left text-sm transition-all ${
                          receiptForm.cashReceiptPurpose === purpose
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${receiptForm.cashReceiptPurpose === purpose ? 'border-primary' : 'border-gray-300'}`}>
                          {receiptForm.cashReceiptPurpose === purpose && <span className="w-2 h-2 rounded-full bg-primary" />}
                        </span>
                        <div>
                          <div className="font-medium">{label}</div>
                          <div className="text-xs text-gray-400 font-normal">{sub}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {receiptForm.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="r-phone">휴대폰번호 <span className="text-red-500">*</span></Label>
                      {isMaskedValue(receiptForm.phoneNumber) ? (
                        <div className="flex items-center gap-2">
                          <Input
                            id="r-phone"
                            type="tel"
                            value={receiptForm.phoneNumber}
                            disabled
                            className="bg-gray-50"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setReceiptForm(prev => ({ ...prev, phoneNumber: '' }))}
                          >
                            변경
                          </Button>
                        </div>
                      ) : (
                        <Input
                          id="r-phone"
                          type="tel"
                          placeholder="010-0000-0000"
                          value={receiptForm.phoneNumber}
                          onChange={e => setReceiptForm(prev => ({ ...prev, phoneNumber: fmtPhone(e.target.value) }))}
                          maxLength={13}
                        />
                      )}
                      <p className="text-xs text-gray-400">국세청에 등록된 휴대폰번호로 소득공제가 처리됩니다.</p>
                    </div>
                  )}

                  {receiptForm.cashReceiptPurpose === 'BUSINESS_EXPENSE' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="r-cash-biz">사업자등록번호 <span className="text-red-500">*</span></Label>
                      {isMaskedValue(receiptForm.cashReceiptBizNo) ? (
                        <div className="flex items-center gap-2">
                          <Input
                            id="r-cash-biz"
                            value={receiptForm.cashReceiptBizNo}
                            disabled
                            className="bg-gray-50"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setReceiptForm(prev => ({ ...prev, cashReceiptBizNo: '' }))}
                          >
                            변경
                          </Button>
                        </div>
                      ) : (
                        <Input
                          id="r-cash-biz"
                          placeholder="000-00-00000"
                          value={receiptForm.cashReceiptBizNo}
                          onChange={e => setReceiptForm(prev => ({ ...prev, cashReceiptBizNo: fmtBizNo(e.target.value) }))}
                          maxLength={12}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 세금계산서 */}
              {receiptForm.receiptType === 'TAX_INVOICE' && (
                <div className="space-y-4 pt-2 border-t">
                  <div className="space-y-1.5">
                    <Label htmlFor="r-tax-biz">사업자등록번호 <span className="text-red-500">*</span></Label>
                    {isMaskedValue(receiptForm.taxInvoiceBizNo) ? (
                      <div className="flex items-center gap-2">
                        <Input
                          id="r-tax-biz"
                          value={receiptForm.taxInvoiceBizNo}
                          disabled
                          className="bg-gray-50"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setReceiptForm(prev => ({ ...prev, taxInvoiceBizNo: '' }));
                            setReceiptBizValidated(false);
                            setReceiptBizStatusWarning(null);
                            setReceiptLookupError(null);
                          }}
                        >
                          변경
                        </Button>
                      </div>
                    ) : (
                      <Input
                        id="r-tax-biz"
                        placeholder="000-00-00000"
                        value={receiptForm.taxInvoiceBizNo}
                        onChange={e => {
                          const formatted = fmtBizNo(e.target.value);
                          setReceiptForm(prev => ({ ...prev, taxInvoiceBizNo: formatted }));
                          setReceiptBizValidated(false);
                          setReceiptBizStatusWarning(null);
                          setReceiptLookupError(null);
                          const raw = formatted.replace(/\D/g, '');
                          if (raw.length === 10) handleReceiptBizStatusCheck(raw);
                        }}
                        maxLength={12}
                      />
                    )}
                    {receiptBizStatusLoading && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        사업자 상태 조회 중...
                      </p>
                    )}
                    {receiptBizStatusWarning && (
                      <Alert className="border-orange-300 bg-orange-50 py-2">
                        <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                        <AlertDescription className="text-orange-700 text-xs">{receiptBizStatusWarning}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-company">상호명 <span className="text-red-500">*</span></Label>
                    <Input
                      id="r-company"
                      placeholder="상호명을 입력하세요"
                      value={receiptForm.companyName}
                      onChange={e => {
                        setReceiptForm(prev => ({ ...prev, companyName: e.target.value }));
                        setReceiptBizValidated(false);
                        setReceiptLookupError(null);
                      }}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-ceo">대표자명 <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2">
                      <Input
                        id="r-ceo"
                        placeholder="대표자명을 입력하세요"
                        value={receiptForm.ceoName}
                        onChange={e => {
                          setReceiptForm(prev => ({ ...prev, ceoName: e.target.value }));
                          setReceiptBizValidated(false);
                          setReceiptLookupError(null);
                        }}
                      />
                      <Button type="button" variant="outline" className="flex-shrink-0" disabled={receiptValidating} onClick={handleReceiptBizValidate}>
                        {receiptValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        <span className="ml-1.5">진위확인</span>
                      </Button>
                    </div>
                    {receiptLookupError && <p className="text-xs text-red-500">{receiptLookupError}</p>}
                    {receiptBizValidated && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        진위확인이 완료되었습니다.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-email">세금계산서 수신 이메일 <span className="text-red-500">*</span></Label>
                    <Input
                      id="r-email"
                      type="email"
                      placeholder="email@example.com"
                      value={receiptForm.invoiceEmail}
                      onChange={e => setReceiptForm(prev => ({ ...prev, invoiceEmail: e.target.value }))}
                    />
                    <p className="text-xs text-gray-400">국세청 전자세금계산서가 이메일로 발송됩니다.</p>
                  </div>

                  <Alert className="border-orange-200 bg-orange-50">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                    <AlertDescription className="text-orange-700 text-xs">
                      세금계산서는 사업자만 신청 가능합니다. 사업자번호·상호명·대표자명 입력 후 진위확인을 완료해 주세요.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* 발행 안함 */}
              {receiptForm.receiptType === 'NONE' && (
                <div className="pt-2 border-t">
                  <Alert className="border-gray-200 bg-gray-50">
                    <AlertDescription className="text-gray-600 text-sm">
                      증빙이 발행되지 않습니다. 나중에 설정에서 언제든지 변경할 수 있습니다.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setReceiptModal({ open: false, targetPlan: null })} disabled={receiptSaving}>
                  취소
                </Button>
                <Button type="button" onClick={handleReceiptProceed} disabled={receiptSaving}>
                  {receiptSaving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                  저장 후 계속
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 변경 확인 Modal */}
      <Dialog open={confirmModal.open} onOpenChange={(o) => !changing && setConfirmModal({ open: o, targetPlan: confirmModal.targetPlan, error: null, proration: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>요금제 변경 확인</DialogTitle>
            <DialogDescription>변경 전 아래 내용을 확인해 주세요.</DialogDescription>
          </DialogHeader>
          {confirmModal.targetPlan && planInfo && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 전</span>
                  <span className="font-semibold">{getPlanMeta(displayPlan).name} 플랜</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 후</span>
                  <span className="font-semibold">{getPlanMeta(confirmModal.targetPlan).name} 플랜</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 유형</span>
                  <Badge className={
                    getChangeType(currentPlan, confirmModal.targetPlan) === 'UPGRADE'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }>
                    {getChangeType(currentPlan, confirmModal.targetPlan) === 'UPGRADE' ? '업그레이드' : '다운그레이드'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">적용 시점</span>
                  <span className="font-semibold">
                    {getChangeType(currentPlan, confirmModal.targetPlan) === 'UPGRADE'
                      ? (selectedPaymentMethod === 'CARD' ? '즉시 적용' : '입금 확인 후 즉시 적용')
                      : (() => {
                          const effectiveDate = downgradeEffectiveDate || estimatedDowngradeEffectiveDate;
                          if (effectiveDate) {
                            const date = new Date(effectiveDate);
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            return `${year}.${month}.${day} 00:00 적용`;
                          }
                          return '다음 달 1일 00:00 적용';
                        })()}
                  </span>
                </div>
              </div>

              {getChangeType(currentPlan, confirmModal.targetPlan) === 'UPGRADE' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-xs">
                    {selectedPaymentMethod === 'CARD'
                      ? '카드 결제 즉시 승인 완료 후 적용됩니다.'
                      : <>업그레이드는 계좌이체 결제 후 관리자 입금 확인 시 즉시 적용됩니다. 계좌 정보는 <strong>결제 안내</strong> 페이지에서 확인하세요.</>}
                  </AlertDescription>
                </Alert>
              )}

              {getChangeType(currentPlan, confirmModal.targetPlan) === 'DOWNGRADE' && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 text-xs">
                    {(() => {
                      const effectiveDate = downgradeEffectiveDate || estimatedDowngradeEffectiveDate;
                      if (effectiveDate) {
                        const date = new Date(effectiveDate);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const lastBenefitDate = new Date(date.getTime() - 86400000);
                        const lastYear = lastBenefitDate.getFullYear();
                        const lastMonth = String(lastBenefitDate.getMonth() + 1).padStart(2, '0');
                        const lastDay = String(lastBenefitDate.getDate()).padStart(2, '0');
                        return `다운그레이드는 ${year}.${month}.${day}부터 적용됩니다. ${lastYear}.${lastMonth}.${lastDay}까지는 현재 플랜 혜택이 유지됩니다.`;
                      }
                      return '다운그레이드는 다음 달 1일부터 적용됩니다. 당월 말까지는 현재 플랜 혜택이 유지됩니다.';
                    })()}
                  </AlertDescription>
                </Alert>
              )}

              {/* 에러 메시지 */}
              {confirmModal.error && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription className="text-xs">{confirmModal.error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmModal({ open: false, targetPlan: null, error: null, proration: null })} disabled={changing}>
                  취소
                </Button>
                <Button onClick={handleConfirmChange} disabled={changing}>
                  {changing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  변경 요청
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 변경 불가 Modal */}
      <Dialog open={blockModal.open} onOpenChange={(o) => setBlockModal({ ...blockModal, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>요금제 변경 불가</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{blockModal.message}</AlertDescription>
            </Alert>
            {blockModal.altPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
                <p className="font-semibold mb-1">대안 제안</p>
                <p>{getPlanMeta(blockModal.altPlan).name} 플랜({getPlanMeta(blockModal.altPlan).monthlyLimit?.toLocaleString()}건)으로 변경해보세요.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              {blockModal.altPlan && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setBlockModal({ open: false, message: '' });
                    if (blockModal.altPlan) setConfirmModal({ open: true, targetPlan: blockModal.altPlan, error: null, proration: null });
                  }}
                >
                  {getPlanMeta(blockModal.altPlan).name} 플랜으로 변경
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => window.location.href = 'mailto:alt.generation@outlook.com?subject=요금제 변경 문의'}
              >
                관리자 문의
              </Button>
              <Button onClick={() => setBlockModal({ open: false, message: '' })}>확인</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 변경 결과 Modal */}
      <Dialog open={resultModal.open} onOpenChange={(o) => setResultModal({ open: o, result: resultModal.result })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              요금제 변경 요청 완료
            </DialogTitle>
          </DialogHeader>
          {resultModal.result && (
            <div className="space-y-4 text-sm">
              <p className="text-gray-700">{resultModal.result.message}</p>
              {(resultModal.result.effectiveDate || resultModal.result.changeType) && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  {resultModal.result.effectiveDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">적용 예정일</span>
                      <span className="font-semibold">
                        {new Date(resultModal.result.effectiveDate).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  )}
                  {resultModal.result.changeType && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">변경 유형</span>
                      <span className="font-semibold">
                        {resultModal.result.changeType === 'UPGRADE' ? '업그레이드' : '다운그레이드'}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {resultModal.result.changeType === 'UPGRADE' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-xs">
                    {selectedPaymentMethod === 'CARD'
                      ? '결제가 완료되었습니다. 플랜이 즉시 적용됩니다.'
                      : '결제 안내 페이지에서 입금 계좌를 확인하고 입금해 주세요. 입금 확인 후 즉시 플랜이 적용됩니다.'}
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setResultModal({ open: false, result: null })}>확인</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 엔터프라이즈 모달 */}
      <Dialog open={enterpriseModalOpen} onOpenChange={(o) => !enterpriseSaving && setEnterpriseModalOpen(o)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>엔터프라이즈 요금제 설정</DialogTitle>
            <DialogDescription>월 사용량을 입력하면 자동으로 가격이 계산됩니다.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* 가격표 안내 */}
            <Alert>
              <AlertDescription className="text-xs space-y-1">
                <div className="font-semibold">💡 엔터프라이즈 요금제 가격표</div>
                {enterprisePricingTiers.length > 0 ? (
                  enterprisePricingTiers.map((tier, index) => (
                    <div key={index}>{tier.tier}: ₩{tier.unitPrice}/건</div>
                  ))
                ) : (
                  <div>가격표 로드 중...</div>
                )}
                <div className="text-gray-500 mt-1">* 사용량이 많아질수록 건당 단가 할인</div>
              </AlertDescription>
            </Alert>

            {/* 현재 계약 정보 */}
            {enterpriseContract && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="font-semibold">현재 계약</div>
                <div className="flex justify-between">
                  <span className="text-gray-500">계약명</span>
                  <span>{enterpriseContract.contractName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">월 사용량</span>
                  <span>{enterpriseContract.monthlyLimit.toLocaleString()}건</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">월 이용료</span>
                  <span>₩{enterpriseContract.monthlyPrice.toLocaleString()}</span>
                </div>
                {enterpriseContract.monthlyPrice > 0 && (
                  <>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>공급가액</span>
                      <span>₩{Math.round(enterpriseContract.monthlyPrice / 1.1).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>부가세</span>
                      <span>₩{Math.round(enterpriseContract.monthlyPrice / 11).toLocaleString()}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">계약 기간</span>
                  <span>{new Date(enterpriseContract.startDate).toLocaleDateString('ko-KR')} ~ {new Date(enterpriseContract.endDate).toLocaleDateString('ko-KR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">자동 갱신</span>
                  <span>{enterpriseContract.autoRenewal ? '활성' : '비활성'}</span>
                </div>
              </div>
            )}

            {/* 사용량 입력 */}
            <div>
              <Label htmlFor="enterprise-usage">월 사용량 (5,001건 ~ 1,000,000건)</Label>
              <Input
                id="enterprise-usage"
                type="number"
                min={5001}
                max={1000000}
                step={500}
                placeholder="6000"
                value={enterpriseUsage}
                onChange={(e) => handleEnterpriseUsageChange(e.target.value)}
                onFocus={(e) => { if (enterpriseUsage === '') setEnterpriseUsage(6000); }}
                disabled={enterpriseSaving}
                className="mt-1"
              />
            </div>

            {/* 자동 계산된 가격 표시 */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">월 이용료</div>
              <div className="text-2xl font-bold text-blue-700">₩{enterprisePrice.toLocaleString()}</div>
              <div className="text-xs text-gray-400">(부가세 포함)</div>
            </div>

            {/* 업그레이드/다운그레이드 표시 */}
            {enterpriseContract && (
              <div className="bg-gray-50 p-4 rounded-lg">
                {enterpriseUsage > enterpriseContract.monthlyLimit ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <TrendingUp className="w-5 h-5" />
                    <div>
                      <div className="font-semibold">업그레이드</div>
                      <div className="text-xs text-gray-600">비례 계산 금액이 청구됩니다</div>
                    </div>
                  </div>
                ) : enterpriseUsage < enterpriseContract.monthlyLimit ? (
                  <div className="flex items-center gap-2 text-orange-700">
                    <TrendingDown className="w-5 h-5" />
                    <div>
                      <div className="font-semibold">다운그레이드</div>
                      <div className="text-xs text-gray-600">청구 기간 다음 날부터 적용됩니다</div>
                      <div className="text-xs text-gray-600">즉시 결제는 발생하지 않습니다</div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* 프롤레이션 정보 표시 (업그레이드 시에만 표시) */}
            {(!enterpriseContract || enterpriseUsage > enterpriseContract.monthlyLimit) && prorationData && prorationData.proratedAmount && (
              <div className="bg-purple-50 p-4 rounded-lg">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setProrationExpanded(!prorationExpanded)}
                >
                  <div className="text-sm font-semibold text-purple-700">
                    {prorationData.proratedAmount.isFullPayment ? '전액 청구 상세' : '비례 계산 상세'}
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-purple-600 transition-transform ${prorationExpanded ? 'rotate-180' : ''}`}
                  />
                </div>
                <div className="mt-2 flex justify-between text-sm bg-purple-200 p-2 rounded">
                  <span className="text-purple-800 font-semibold">총 결제 금액</span>
                  <span className="text-purple-800 font-bold">₩{prorationData.proratedAmount.totalPaymentAmount.toLocaleString()}</span>
                </div>
                {prorationExpanded && (
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">현재 플랜 가격</span>
                      <span className="font-medium">₩{prorationData.proratedAmount.currentPlanPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">새 플랜 가격</span>
                      <span className="font-medium">₩{prorationData.proratedAmount.newPlanPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">청구 기간</span>
                      <span className="font-medium">
                        {prorationData.proratedAmount.remainingDays}일 ({new Date().toISOString().split('T')[0]}~{prorationData.proratedAmount.billingEndDate})
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">청구 주기일</span>
                      <span className="font-medium">매월 {prorationData.proratedAmount.billingCycleDay}일</span>
                    </div>
                    <div className="flex justify-between text-sm bg-purple-100 p-2 rounded">
                      <span className="text-purple-700 font-semibold">비례 계산 금액</span>
                      <span className="text-purple-700 font-bold">₩{prorationData.proratedAmount.proratedAmount.toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-purple-600 bg-purple-100 p-2 rounded">
                      (₩{prorationData.proratedAmount.newPlanPrice.toLocaleString()} × {prorationData.proratedAmount.remainingDays}일 ÷ 30일)
                    </div>
                    {prorationData.proratedAmount.isMinimumAmountApplied && (
                      <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
                        * PG사 최소 결제 금액(100원)으로 조정되었습니다.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 결제 방법 선택 */}
              <div>
                <Label className="text-sm font-medium mb-2 block">결제 방법</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setEnterprisePaymentMethod('BANK_TRANSFER')}
                    className={`p-2 rounded-lg border-2 text-left transition-all ${
                      enterprisePaymentMethod === 'BANK_TRANSFER'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-600" />
                      <span className="font-semibold text-gray-800 text-sm">계좌이체</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">무통장 입금</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => isCardPaymentEnabled && setEnterprisePaymentMethod('CARD')}
                    disabled={!isCardPaymentEnabled}
                    className={`p-2 rounded-lg border-2 text-left transition-all ${
                      !isCardPaymentEnabled
                        ? 'border-gray-100 opacity-50 cursor-not-allowed'
                        : enterprisePaymentMethod === 'CARD'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-600" />
                      <span className="font-semibold text-gray-800 text-sm">카드 결제</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {!isCardPaymentEnabled
                        ? '곧 서비스 예정'
                        : cards.length > 0 ? `등록된 카드 ${cards.length}장` : '카드 등록 필요'}
                    </div>
                  </button>
                </div>
                {enterprisePaymentMethod === 'BANK_TRANSFER' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700 mt-2">
                    <p className="font-semibold mb-1">• 가상계좌 이용 안내</p>
                    <p>• 입금 승인 시점에 이용기간, 청구기간, 청구 주기일이 확정됩니다</p>
                    <p>• 관리자 입금 확인 승인 시 청구 주기일이 설정됩니다</p>
                  </div>
                )}
                {enterprisePaymentMethod === 'CARD' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700 mt-2">
                    <p className="font-semibold mb-1">• 카드 결제 이용 안내</p>
                    <p>• 결제 즉시 이용기간, 청구기간, 청구 주기일이 확정됩니다</p>
                    <p>• 청구 주기일은 오늘 날짜로 설정됩니다</p>
                  </div>
                )}
              </div>
 
            {/* 카드 결제 선택 시 등록된 카드 목록 */}
            {enterprisePaymentMethod === 'CARD' && cards.length > 0 && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-semibold text-gray-700 mb-2">등록된 카드</div>
                <div className="space-y-2">
                  {cards.map((card) => (
                    <div key={card.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{card.cardCompany}</span>
                        <span className="text-sm text-gray-500">{card.cardNumberMasked}</span>
                        {card.isDefault && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">기본</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 계약 기간 안내 - 저장 전 반드시 고지 (기존 계약 수정 시에도 계약기간이 오늘부터 1년으로 재설정됨) */}
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800">
                <span className="font-semibold">
                  이 계약은 오늘({new Date().toLocaleDateString('ko-KR')})부터 1년 후인{' '}
                  {new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate()).toLocaleDateString('ko-KR')}까지 유지됩니다.
                </span>{' '}
                {enterpriseContract
                  ? '사용량을 변경해 저장하면 계약 기간도 오늘부터 다시 1년으로 갱신됩니다.'
                  : '아래 "자동 갱신"을 켜면 계약 만료 시 자동으로 1년 연장되고, 끄면 만료일에 계약이 종료됩니다.'}
              </AlertDescription>
            </Alert>

            {/* 자동 갱신 설정 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoRenewal"
                checked={enterpriseAutoRenewal}
                onChange={(e) => setEnterpriseAutoRenewal(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="autoRenewal" className="text-sm text-gray-700 cursor-pointer">
                계약 만료 시 자동 갱신
              </label>
            </div>

            {/* 저장 버튼 */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEnterpriseModalOpen(false)}
                disabled={enterpriseSaving}
              >
                취소
              </Button>
              <Button onClick={handleEnterpriseSave} disabled={enterpriseSaving || !!enterpriseError || enterprisePrice === 0}>
                {enterpriseSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {enterprisePaymentMethod === 'CARD' ? '결제하기' : '저장하고 입금하기'}
              </Button>
            </div>

            {/* 에러 메시지 */}
            {enterpriseError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-xs">{enterpriseError}</AlertDescription>
              </Alert>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 결제 취소 확인 Dialog */}
      <Dialog open={cancelPaymentConfirm.open} onOpenChange={(open) => !cancellingPayment && setCancelPaymentConfirm({ open, paymentId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>결제 신청 취소</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            결제 신청을 취소하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogDescription>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setCancelPaymentConfirm({ open: false, paymentId: null })} disabled={cancellingPayment}>
              취소
            </Button>
            <Button onClick={handleConfirmCancelPayment} disabled={cancellingPayment}>
              {cancellingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              확인
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 다운그레이드 결과 Dialog */}
      <Dialog open={downgradeResult.open} onOpenChange={(open) => setDowngradeResult({ ...downgradeResult, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>다운그레이드 예약 완료</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DialogDescription>
              {downgradeResult.message}
            </DialogDescription>
            {downgradeResult.effectiveDate && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">적용일</span>
                  <span className="font-semibold">{downgradeResult.effectiveDate}</span>
                </div>
                {downgradeResult.nextBillingAmount && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">다음 청구 금액</span>
                    <span className="font-semibold">₩{downgradeResult.nextBillingAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
            <Button onClick={() => setDowngradeResult({ open: false, message: '' })}>
              확인
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 결제 방법 선택 Dialog */}
      <Dialog open={paymentMethodModal.open} onOpenChange={(open) => !processingPayment && setPaymentMethodModal({ open, targetPlan: null, proration: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>결제 방법 선택</DialogTitle>
            <DialogDescription>
              {paymentMethodModal.targetPlan && getPlanMeta(paymentMethodModal.targetPlan).name} 플랜으로 변경하기 위한 결제 방법을 선택하세요.
            </DialogDescription>
          </DialogHeader>
          {/* 프로레이션 정보 표시 */}
          {paymentMethodModal.proration && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setPaymentProrationExpanded(!paymentProrationExpanded)}
              >
                <h4 className="font-semibold text-sm text-blue-800">
                  {paymentMethodModal.proration.isFullPayment ? '플랜 변경 비용 (전액 청구)' : '플랜 변경 비용 (프롤레이션 적용)'}
                </h4>
                <ChevronDown
                  className={`w-4 h-4 text-blue-600 transition-transform ${paymentProrationExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              <div className="mt-2 flex justify-between text-sm bg-blue-200 p-2 rounded">
                <span className="text-blue-800 font-semibold">총 결제 금액</span>
                <span className="text-blue-800 font-bold">₩{paymentMethodModal.proration.calculationDetails ? paymentMethodModal.proration.prorationAmount.toLocaleString() : paymentMethodModal.proration.newPrice.toLocaleString()}</span>
              </div>
              {paymentProrationExpanded && (
                <div className="mt-3 space-y-1 text-xs text-blue-700">
                  <div className="flex justify-between">
                    <span>현재 플랜:</span>
                    <span className="font-medium">₩{paymentMethodModal.proration.currentPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>새 플랜:</span>
                    <span className="font-medium">₩{paymentMethodModal.proration.newPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>청구 기간:</span>
                    <span className="font-medium">
                      {paymentMethodModal.proration.calculationDetails
                        ? `${paymentMethodModal.proration.calculationDetails.startDate} ~ ${paymentMethodModal.proration.calculationDetails.endDate}`
                        : paymentMethodModal.proration.billingCycleDay
                          ? (() => {
                              const today = new Date();
                              const billingCycleDay = paymentMethodModal.proration.billingCycleDay;
                              let startDate: Date;
                              let endDate: Date;

                              if (today.getDate() >= billingCycleDay) {
                                startDate = new Date(today.getFullYear(), today.getMonth(), billingCycleDay);
                                endDate = new Date(today.getFullYear(), today.getMonth() + 1, billingCycleDay - 1);
                              } else {
                                startDate = new Date(today.getFullYear(), today.getMonth() - 1, billingCycleDay);
                                endDate = new Date(today.getFullYear(), today.getMonth(), billingCycleDay - 1);
                              }

                              const formatDate = (date: Date) => {
                                const month = (date.getMonth() + 1).toString();
                                const day = date.getDate().toString();
                                return `${month}.${day}`;
                              };

                              return `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
                            })()
                          : `${paymentMethodModal.proration.remainingDays}일`}
                    </span>
                  </div>
                  {paymentMethodModal.proration.calculationDetails ? (
                    <>
                      <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                        <span>비례 계산 차액:</span>
                        <span className="font-medium">
                          ₩{paymentMethodModal.proration.prorationAmount.toLocaleString()}
                          <span className="text-blue-600 ml-1">
                            ({paymentMethodModal.proration.calculationDetails.formula})
                          </span>
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                      <span>청구 금액:</span>
                      <span className="font-medium">
                        ₩{paymentMethodModal.proration.newPrice.toLocaleString()}
                        <span className="text-blue-600 ml-1">
                          (전액 청구)
                        </span>
                      </span>
                    </div>
                  )}
                  {paymentMethodModal.proration.billingCycleDay && (
                    <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded mt-2">
                      청구 주기일이 매월 {paymentMethodModal.proration.billingCycleDay}일로 변경됩니다.
                      {paymentMethodModal.proration.calculationDetails && (
                        <span className="block mt-1">
                          이용 기간: {paymentMethodModal.proration.calculationDetails.startDate} ~ {paymentMethodModal.proration.calculationDetails.endDate}
                        </span>
                      )}
                    </div>
                  )}
                  {paymentMethodModal.proration.warningMessage && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded mt-2">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{paymentMethodModal.proration.warningMessage}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="space-y-3 mt-3">
            {/* 계좌이체 */}
            <button
              type="button"
              onClick={() => handlePaymentMethodSelect('BANK_TRANSFER')}
              disabled={processingPayment}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                selectedPaymentMethod === 'BANK_TRANSFER'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-800 text-sm">계좌이체</div>
                <div className="text-xs text-gray-500">입금 후 관리자 확인</div>
              </div>
              {selectedPaymentMethod === 'BANK_TRANSFER' && (
                <CheckCircle2 className="w-5 h-5 text-blue-500" />
              )}
            </button>
            {selectedPaymentMethod === 'BANK_TRANSFER' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 text-xs text-orange-700">
                <p className="font-semibold mb-1">• 가상계좌 이용 안내</p>
                <p>• 입금 승인 시점에 이용기간, 청구기간, 청구 주기일이 확정됩니다</p>
                <p>• 관리자 입금 확인 승인 시 청구 주기일이 설정됩니다</p>
              </div>
            )}

            {/* 카드 결제 */}
            <button
              type="button"
              onClick={() => isCardPaymentEnabled && handlePaymentMethodSelect('CARD')}
              disabled={processingPayment || !isCardPaymentEnabled}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                !isCardPaymentEnabled
                  ? 'border-gray-100 opacity-50 cursor-not-allowed'
                  : selectedPaymentMethod === 'CARD'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-800 text-sm">카드 결제</div>
                <div className="text-xs text-gray-500">
                  {!isCardPaymentEnabled
                    ? '곧 서비스 예정'
                    : cards.length > 0 ? `등록된 카드 ${cards.length}장` : '카드 등록 필요'}
                </div>
              </div>
              {selectedPaymentMethod === 'CARD' && (
                <CheckCircle2 className="w-5 h-5 text-blue-500" />
              )}
            </button>
            {selectedPaymentMethod === 'CARD' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-700">
                <p className="font-semibold mb-1">• 카드 결제 이용 안내</p>
                <p>• 결제 즉시 이용기간, 청구기간, 청구 주기일이 확정됩니다</p>
                <p>• 청구 주기일은 오늘 날짜로 설정됩니다</p>
              </div>
            )}

            {/* 등록된 카드 목록 - default 카드 1개만 표시 */}
            {cards.filter(card => card.isDefault).length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-700 mb-2">등록된 카드</div>
                <div className="space-y-2">
                  {cards.filter(card => card.isDefault).map((card) => (
                    <div key={card.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-700">{card.cardCompany}</span>
                        <span className="text-xs text-gray-500">{card.cardNumberMasked}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">기본</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 안내 메시지 */}
            <Alert className="border-blue-200 bg-blue-50 py-2">
              <AlertCircle className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-700 text-xs">
                카드 결제를 선택하면 매월 자동으로 결제됩니다. 빌링키는 PG사가 안전하게 보관합니다.
              </AlertDescription>
            </Alert>

            {/* 취소 버튼 */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setPaymentMethodModal({ open: false, targetPlan: null, proration: null })}
                disabled={processingPayment}
              >
                취소
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 카드 등록 Dialog */}
      <Dialog open={cardRegisterModal} onOpenChange={(open) => !cardRegistering && setCardRegisterModal(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>카드 등록</DialogTitle>
            <DialogDescription>
              정기 결제를 위한 카드 정보를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="card-number">카드번호</Label>
              <Input
                id="card-number"
                placeholder="1234-5678-9012-3456"
                value={formatCardNumber(cardForm.cardNumber)}
                onChange={(e) => setCardForm({ ...cardForm, cardNumber: e.target.value.replace(/\D/g, '') })}
                disabled={cardRegistering}
                maxLength={19}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="card-expiry">유효기간</Label>
              <Input
                id="card-expiry"
                placeholder="MM/YY"
                value={formatCardExpiration(cardForm.cardExpiration)}
                onChange={(e) => setCardForm({ ...cardForm, cardExpiration: e.target.value.replace(/\D/g, '') })}
                disabled={cardRegistering}
                maxLength={5}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="customer-identity-number">생년월일 (YYMMDD) 또는 사업자등록번호</Label>
              <Input
                id="customer-identity-number"
                name="customer-identity-number"
                placeholder="900101 또는 10자리 사업자번호"
                value={cardForm.customerIdentityNumber}
                onChange={(e) => setCardForm({ ...cardForm, customerIdentityNumber: e.target.value.replace(/\D/g, '') })}
                disabled={cardRegistering}
                maxLength={10}
                autoComplete="new-password"
                defaultValue=""
                readOnly
                onFocus={(e) => e.target.readOnly = false}
              />
            </div>
            <div>
              <Label htmlFor="card-password">카드 비밀번호 (앞 2자리)</Label>
              <Input
                id="card-password"
                placeholder="**"
                type="password"
                value={cardForm.cardPassword}
                onChange={(e) => setCardForm({ ...cardForm, cardPassword: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                disabled={cardRegistering}
                maxLength={2}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="card-owner">카드 소유자명</Label>
              <Input
                id="card-owner"
                placeholder="홍길동"
                value={cardForm.cardOwnerName}
                onChange={(e) => setCardForm({ ...cardForm, cardOwnerName: e.target.value })}
                disabled={cardRegistering}
                autoComplete="off"
              />
            </div>

            {/* 에러 메시지 */}
            {cardRegisterError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-xs">{cardRegisterError}</AlertDescription>
              </Alert>
            )}

            {/* 버튼 */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCardRegisterModal(false)}
                disabled={cardRegistering}
              >
                취소
              </Button>
              <Button onClick={handleCardRegister} disabled={cardRegistering}>
                {cardRegistering && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                등록
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 카드 삭제 확인 Dialog */}
      <Dialog open={cardDeleteConfirm.open} onOpenChange={(open) => setCardDeleteConfirm({ open, cardId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>카드 삭제</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            카드를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogDescription>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setCardDeleteConfirm({ open: false, cardId: null })}>
              취소
            </Button>
            <Button onClick={handleConfirmCardDelete}>
              확인
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
